/**
 * Ad-campaign keyword research panel (P2.9b).
 *
 * The "Build ad campaigns" tab on the unified Keyword Research page.
 * Calls the standalone /google-ads/keyword-research endpoint and renders
 * the resulting clusters as cards. Read-only — no DB writes, no campaign
 * run created. Operator can take the clusters from here into the
 * Campaigns tab on /ads-tools to actually create the campaign.
 *
 * Differs from the SEO "Blog topics" tab in three ways the operator
 * should notice immediately:
 *   1. Output is clusters (not flat keywords) — each cluster maps to
 *      a future ad-group with its own landing page
 *   2. Volumes shown are search volume + per-keyword CPC (since paid
 *      bids matter, not just SEO volume)
 *   3. Sales-intent only — info-intent / generic keywords are dropped
 *      by the pipeline's filter agent before they reach this view
 */
import { fuzzyFilter } from '@/lib/fuzzyMatch';
import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  CheckCircle2,
  ChevronDown,
  Copy,
  Download,
  ExternalLink,
  FileSpreadsheet,
  History,
  Loader2,
  Mail,
  Megaphone,
  Search,
  Sparkles,
  Swords,
  XCircle,
  TriangleAlert,
} from 'lucide-react';
// Comms 9/23: alerts on this page stay until closed (see stickyToast).
import { stickyToast as toast } from '@/features/ads/stickyToast';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Textarea } from '@/components/ui/textarea';
import {
  Tooltip as UiTooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { LANGUAGES } from './geoConstants';
import { LocationPicker } from './LocationPicker';
import { SuggestedNegativesPanel } from './SuggestedNegativesPanel';
import type {
  GeoTargetSuggestion,
  KeywordResearchQualityCheck,
  NegativeKeywordCandidate,
  PriorKeywordUse,
  SitePageSuggestion,
  TargetKeywordSuggestion,
} from '@/services/googleAdsCampaignsService';
import {
  fetchBrandDefenseProbe,
  fetchSponsoredAdProbe,
  ingestBookmarkletCapture,
  analyzeSponsoredAds,
  type BookmarkletCaptureResult,
  type SponsoredAdAnalysis,
  type SponsoredAdSeen,
  keywordResearchCampaignHandoffKey,
  previewSitePageSuggestions,
  previewSeedKeywordAutomation,
  saveKeywordResearchSelection,
  saveCompanyInfoBrief,
  synthesizeCompanyInfo,
  type KeywordResearchCampaignHandoff,
} from '@/services/googleAdsCampaignsService';
import {
  buildSponsoredAdBookmarklet,
  buildGoogleSearchUrl,
  encodeUule,
  parseSponsoredAdHash,
} from './sponsoredAdBookmarklet';
import {
  fetchTenantPages,
  normalizeUrl,
  previewAdCampaignKeywords,
  type PreviewedCluster,
  type PreviewedKeyword,
} from '@/services/googleAdsCampaignsService';
import {
  buildClustersCsv,
  buildKeywordPlannerTrendData,
  type PlannerTrendPoint,
} from './keywordPlannerExport';
import {
  useActiveResearchRun,
  useResearchHistory,
  useResearchRun,
} from '@/hooks/useKeywordResearchHistory';
import {
  keywordResearchHistoryQueryKeys,
  type KeywordResearchHistoryItem,
} from '@/services/keywordResearchHistoryService';
import { useTenantStore } from '@/stores/useTenantStore';

interface AdCampaignKeywordsPanelProps {
  defaultCompanyInformation?: string;
  defaultPages?: string[];
  workspaceName?: string;
  workspaceServices?: string[];
  workspaceLocations?: string[];
  /** CORE-K-09: the subset of workspaceLocations that is Brand Bible
   *  `service_locations` — the only rows the picker's modal may edit. */
  editableWorkspaceLocations?: string[];
  hideClusterResults?: boolean;
  /** ISO timestamp of when the saved keyword-research brief was last
   *  generated. Used for the "saved X ago" hint under the textarea.
   *  Null when the tenant has no brief yet. */
  defaultBriefGeneratedAt?: string | null;
}

const KEYWORD_RESEARCH_RESULT_CACHE_VERSION = 1;
const DEFAULT_VISIBLE_CLUSTER_COUNT = 3;

interface KeywordResearchResultCache {
  version: number;
  tenant_id: string;
  saved_at: string;
  inputs: {
    company_information: string;
    pages: string;
    seed_keywords: string;
    supervised_seeds: boolean;
    language: string;
    locations: GeoTargetSuggestion[];
  };
  result: {
    run_id: string | null;
    clusters: PreviewedCluster[];
    negative_keywords: NegativeKeywordCandidate[];
    quality_checks: KeywordResearchQualityCheck[];
    target_keyword_suggestions: TargetKeywordSuggestion[];
    planner_language: string | null;
    planner_geo_target_constants: string[];
    planner_site_filter_url: string | null;
    sponsored_ad_note?: string | null;
  };
}

function keywordResearchResultCacheKey(tenantId: string): string {
  return `google_ads_keyword_research_last_result:${tenantId}`;
}

function readKeywordResearchResultCache(
  tenantId: string,
): KeywordResearchResultCache | null {
  if (!tenantId || typeof window === 'undefined') return null;
  const raw = window.localStorage.getItem(keywordResearchResultCacheKey(tenantId));
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as KeywordResearchResultCache;
    if (
      parsed.version !== KEYWORD_RESEARCH_RESULT_CACHE_VERSION ||
      parsed.tenant_id !== tenantId ||
      !Array.isArray(parsed.result?.clusters)
    ) {
      return null;
    }
    return parsed;
  } catch {
    window.localStorage.removeItem(keywordResearchResultCacheKey(tenantId));
    return null;
  }
}

function writeKeywordResearchResultCache(cache: KeywordResearchResultCache) {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(
    keywordResearchResultCacheKey(cache.tenant_id),
    JSON.stringify(cache),
  );
}

function clearKeywordResearchResultCache(tenantId: string) {
  if (!tenantId || typeof window === 'undefined') return;
  window.localStorage.removeItem(keywordResearchResultCacheKey(tenantId));
}

export function AdCampaignKeywordsPanel({
  defaultCompanyInformation = '',
  defaultPages = [],
  workspaceName,
  workspaceServices = [],
  workspaceLocations = [],
  editableWorkspaceLocations,
  hideClusterResults = false,
  defaultBriefGeneratedAt = null,
}: AdCampaignKeywordsPanelProps) {
  const tenantId = useTenantStore((s) => s.currentTenant ?? '');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const defaultPagesText = defaultPages.join('\n');
  const [companyInfo, setCompanyInfo] = useState(defaultCompanyInformation);
  const [pages, setPages] = useState(defaultPagesText);

  // Brand bible is async (parent fetches it via TanStack Query). It
  // can change after mount when:
  //   - First load: defaultCompanyInformation starts as '' while the
  //     query is in flight, populates when the data lands.
  //   - Tenant switch: user picks a different tenant in the topbar,
  //     parent fetches its brand bible, prop changes.
  // useState only reads the prop on initial mount, so without this
  // effect the field stays empty / shows the prior tenant's value.
  // Behavior: sync when the prop value changes AND the user hasn't
  // typed anything custom yet (i.e., field is empty or matches the
  // OLD default). Don't clobber the operator's active typing.
  const lastSyncedDefaultRef = useRef<string>(defaultCompanyInformation);
  useEffect(() => {
    if (defaultCompanyInformation === lastSyncedDefaultRef.current) {
      return;
    }
    if (!defaultCompanyInformation.trim() && companyInfo.trim()) {
      lastSyncedDefaultRef.current = defaultCompanyInformation;
      return;
    }
    // Only auto-populate if the field is empty OR currently shows the
    // previous default (i.e., the user hasn't edited it).
    if (
      companyInfo === '' ||
      companyInfo === lastSyncedDefaultRef.current
    ) {
      setCompanyInfo(defaultCompanyInformation);
    }
    lastSyncedDefaultRef.current = defaultCompanyInformation;
  }, [defaultCompanyInformation, companyInfo]);

  const lastSyncedPagesDefaultRef = useRef<string>(defaultPagesText);
  useEffect(() => {
    if (defaultPagesText === lastSyncedPagesDefaultRef.current) {
      return;
    }
    if (pages === '' || pages === lastSyncedPagesDefaultRef.current) {
      setPages(defaultPagesText);
    }
    lastSyncedPagesDefaultRef.current = defaultPagesText;
  }, [defaultPagesText, pages]);
  const [seedKeywords, setSeedKeywords] = useState('');
  const [supervisedSeeds, setSupervisedSeeds] = useState(false);
  const [seedReviewApplied, setSeedReviewApplied] = useState(false);
  const [seedAutomationSuggestions, setSeedAutomationSuggestions] = useState<TargetKeywordSuggestion[]>([]);
  const [seedPriorUse, setSeedPriorUse] = useState<Record<string, PriorKeywordUse>>({});
  // Default OFF. Hiding seeds by default is how this feature has gone wrong
  // before — the operator opts in, and the count of held-back seeds is shown.
  const [newSeedsOnly, setNewSeedsOnly] = useState(false);
  const [sponsoredAdQuery, setSponsoredAdQuery] = useState('seo company');
  const [sitePageSuggestions, setSitePageSuggestions] = useState<SitePageSuggestion[]>([]);
  const [sitePageReviewApplied, setSitePageReviewApplied] = useState(false);
  const [clusters, setClusters] = useState<PreviewedCluster[]>([]);
  const [qualityChecks, setQualityChecks] = useState<KeywordResearchQualityCheck[]>([]);
  const [targetKeywordSuggestions, setTargetKeywordSuggestions] = useState<TargetKeywordSuggestion[]>([]);
  const [plannerLanguage, setPlannerLanguage] = useState<string | null>(null);
  const [plannerGeoTargetConstants, setPlannerGeoTargetConstants] = useState<string[]>([]);
  const [plannerSiteFilterUrl, setPlannerSiteFilterUrl] = useState<string | null>(null);
  const [sponsoredAdNote, setSponsoredAdNote] = useState<string | null>(null);
  const [showSponsoredAds, setShowSponsoredAds] = useState(false);
  const [researchRunId, setResearchRunId] = useState<string | null>(null);
  // Preferred ad-group count. Empty string = no preference, which is the
  // default and leaves the clustering prompt untouched.
  const [targetClusterCount, setTargetClusterCount] = useState<string>('');
  const [cachedResultSavedAt, setCachedResultSavedAt] = useState<string | null>(
    null,
  );
  // Phase C — auto-classified negative-keyword suggestions
  const [negatives, setNegatives] = useState<NegativeKeywordCandidate[]>([]);

  // Phase D.2 — brand-defense probe. Lightweight Serper call gated
  // by KEYWORD_RESEARCH_BRAND_DEFENSE_PROBE_ENABLED. Fires once on
  // mount when a tenant is selected; banner renders only when
  // competitors are actually bidding on the brand.
  const brandDefenseQuery = useQuery({
    queryKey: ['brand-defense', tenantId],
    queryFn: () => fetchBrandDefenseProbe(tenantId),
    enabled: !!tenantId,
    staleTime: 30 * 60 * 1000,
  });
  const brandDefense = brandDefenseQuery.data;
  const sponsoredAdQueryState = useQuery({
    queryKey: ['sponsored-ads', tenantId, sponsoredAdQuery],
    queryFn: () => fetchSponsoredAdProbe(sponsoredAdQuery.trim()),
    enabled: false,
    staleTime: 10 * 60 * 1000,
  });
  useEffect(() => {
    const data = sponsoredAdQueryState.data;
    if (!data?.sponsored_results?.length) return;
    const note = buildSponsoredAdNote(data);
    setSponsoredAdNote(note);
    const cache = readKeywordResearchResultCache(tenantId);
    if (!cache) return;
    if (cache.result.sponsored_ad_note === note) return;
    writeKeywordResearchResultCache({
      ...cache,
      result: {
        ...cache.result,
        sponsored_ad_note: note,
      },
    });
  }, [tenantId, sponsoredAdQueryState.data]);
  // Phase B — language for Keyword Planner. Empty string is the
  // "auto-detect from tenant locale" sentinel; backend reads
  // tenant_settings.country/region when blank.
  const [language, setLanguage] = useState<string>('');
  // Phase E.1 — multi-select locations via Google Ads geo target catalog.
  // Empty array → backend resolves from tenant locale.
  const [locations, setLocations] = useState<GeoTargetSuggestion[]>([]);
  const locationSeedNames = useMemo(
    () => buildLocationSeedNames(locations, workspaceLocations),
    [locations, workspaceLocations],
  );
  const locationSeedKey = locationSeedNames.join('|');

  useEffect(() => {
    setSeedReviewApplied(false);
    setSeedAutomationSuggestions([]);
    setSeedPriorUse({});
  }, [tenantId, companyInfo, locationSeedKey]);

  useEffect(() => {
    setSitePageSuggestions([]);
    setSitePageReviewApplied(false);
  }, [tenantId]);

  useEffect(() => {
    const cache = readKeywordResearchResultCache(tenantId);
    if (!cache) {
      setClusters([]);
      setNegatives([]);
      setQualityChecks([]);
      setTargetKeywordSuggestions([]);
      setPlannerLanguage(null);
      setPlannerGeoTargetConstants([]);
      setPlannerSiteFilterUrl(null);
      setSponsoredAdNote(null);
      setResearchRunId(null);
      setCachedResultSavedAt(null);
      return;
    }
    setCompanyInfo(cache.inputs.company_information);
    setPages(cache.inputs.pages);
    setSeedKeywords(cache.inputs.seed_keywords);
    setSupervisedSeeds(cache.inputs.supervised_seeds);
    setLanguage(cache.inputs.language);
    setLocations(cache.inputs.locations ?? []);
    setResearchRunId(cache.result.run_id);
    setClusters(cache.result.clusters);
    setNegatives(cache.result.negative_keywords ?? []);
    setQualityChecks(cache.result.quality_checks ?? []);
    setTargetKeywordSuggestions(cache.result.target_keyword_suggestions ?? []);
    setPlannerLanguage(cache.result.planner_language);
    setPlannerGeoTargetConstants(cache.result.planner_geo_target_constants ?? []);
    setPlannerSiteFilterUrl(cache.result.planner_site_filter_url);
    setCachedResultSavedAt(cache.saved_at);
  }, [tenantId]);

  const tenantPagesForPlaceholderQuery = useQuery({
    queryKey: ['tenant-pages-placeholder', tenantId],
    queryFn: () => fetchTenantPages(tenantId),
    enabled: !!tenantId && defaultPages.length === 0,
    staleTime: 5 * 60 * 1000,
  });
  const tenantPageExamples = tenantPagesForPlaceholderQuery.data?.pages ?? [];
  const tenantPagesText = tenantPageExamples.join('\n');

  useEffect(() => {
    if (defaultPages.length > 0 || !tenantPagesText || pages.trim()) {
      return;
    }
    setPages(tenantPagesText);
  }, [defaultPages.length, pages, tenantPagesText]);

  const seedKeywordPlaceholder = useMemo(
    () =>
      buildSeedKeywordPlaceholder({
        businessName: workspaceName,
        services: workspaceServices,
        locations: workspaceLocations,
        tenantId,
      }),
    [tenantId, workspaceLocations, workspaceName, workspaceServices],
  );

  const pagePlaceholder = useMemo(
    () =>
      buildPagePlaceholder({
        tenantId,
        pages: tenantPageExamples,
      }),
    [tenantId, tenantPageExamples],
  );

  // Saved-brief timestamp — used for the "saved X ago" hint.
  // Initial value from the parent (brand_bible.keyword_research_brief_generated_at);
  // updated by both autoFillMutation (backend persists on synth) and
  // saveMutation (explicit operator save).
  const [briefGeneratedAt, setBriefGeneratedAt] = useState<string | null>(
    defaultBriefGeneratedAt ?? null,
  );
  // Sync the timestamp when the prop changes (tenant switch / initial load)
  useEffect(() => {
    setBriefGeneratedAt(defaultBriefGeneratedAt ?? null);
  }, [defaultBriefGeneratedAt]);

  // "Write brief from research" agent — synthesizes a 2-3 sentence
  // company description from brand bible + indexed pages + cached
  // home-page scrape. Populates the Company information field on
  // success AND persists to brand_bible.keyword_research_brief so
  // the brief survives page refreshes.
  const autoFillMutation = useMutation({
    mutationFn: () => synthesizeCompanyInfo(tenantId),
    onSuccess: (result) => {
      if (!result.company_information || result.company_information.length < 10) {
        toast.warning(
          'No usable signals found. Add brand bible content or index some pages, then try again.',
        );
        return;
      }
      setCompanyInfo(result.company_information);
      if (result.generated_at) {
        setBriefGeneratedAt(result.generated_at);
      }
      const srcCount = result.sources_used?.length ?? 0;
      const savedTag = result.saved_to_brand_bible ? ' · saved' : '';
      toast.success(
        `Company info synthesized from ${srcCount} source${srcCount !== 1 ? 's' : ''}${savedTag}`,
      );
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Auto-fill failed: ${detail}`);
    },
  });

  // Explicit save — for when the operator manually edits the
  // textarea after auto-fill (or types from scratch). Persists to
  // brand_bible.keyword_research_brief.
  const saveMutation = useMutation({
    mutationFn: () =>
      saveCompanyInfoBrief({
        tenantId,
        companyInformation: companyInfo.trim(),
      }),
    onSuccess: (result) => {
      if (result.generated_at) {
        setBriefGeneratedAt(result.generated_at);
      }
      toast.success('Brief saved');
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Save failed: ${detail}`);
    },
  });

  // `silent` is the auto-load on arrival: it fills the suggestions panel
  // without a toast, so a page visit doesn't open an alert that has to be
  // closed by hand. The button passes `silent: false`.
  const seedPreviewMutation = useMutation<
    Awaited<ReturnType<typeof previewSeedKeywordAutomation>>,
    unknown,
    { silent: boolean }
  >({
    mutationFn: () =>
      previewSeedKeywordAutomation({
        tenant_id: tenantId,
        company_information: companyInfo.trim(),
        existing_target_keywords: splitLines(seedKeywords),
        pages: splitLines(pages),
        location_names: locationSeedNames,
      }),
    onSuccess: (result, { silent }) => {
      setSeedAutomationSuggestions(result.suggestions);
      setSeedPriorUse(result.prior_use ?? {});
      setSeedReviewApplied(false);
      if (silent) return;
      if (result.suggestions.length === 0) {
        toast.warning(
          'No keyword ideas found. Add services in the Brand Bible or type your own keywords.',
        );
        return;
      }
      toast.success(
        `${result.suggestions.length} keyword idea${result.suggestions.length !== 1 ? 's' : ''} found`,
      );
    },
    onError: (err: unknown, { silent }) => {
      // A failed auto-load shows inline in the suggestions panel instead.
      if (silent) return;
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Could not load keyword ideas: ${detail}`);
    },
  });

  // Comms 9/23: suggested keywords show without extra clicks. Load them on
  // arrival and again after the inputs they depend on settle (the reset
  // effect above clears them on every change). The preview only reads the
  // workspace; it stops before Keyword Planner, Ahrefs and clustering.
  const { mutate: previewSeeds } = seedPreviewMutation;
  const autoSeedKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (!tenantId) return;
    const key = `${tenantId}|${companyInfo}|${locationSeedKey}`;
    const timer = window.setTimeout(() => {
      if (autoSeedKeyRef.current === key) return;
      autoSeedKeyRef.current = key;
      previewSeeds({ silent: true });
    }, 800);
    return () => window.clearTimeout(timer);
  }, [tenantId, companyInfo, locationSeedKey, previewSeeds]);

  const sitePagePreviewMutation = useMutation({
    mutationFn: () =>
      previewSitePageSuggestions({
        tenant_id: tenantId,
        existing_pages: splitLines(pages),
        existing_target_keywords: splitLines(seedKeywords),
        limit: 10,
      }),
    onSuccess: (result) => {
      setSitePageSuggestions(result.suggestions);
      setSitePageReviewApplied(false);
      if (result.suggestions.length === 0) {
        toast.warning(
          'No new landing-page suggestions found. Add or index service pages, then try again.',
        );
        return;
      }
      toast.success(
        `${result.suggestions.length} suggested page${result.suggestions.length !== 1 ? 's' : ''} found`,
      );
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Could not suggest pages: ${detail}`);
    },
  });

  /** Seeds this account has never been researched with. */
  const isNewSeed = (keyword: string) =>
    seedPriorUse[keyword]?.is_new !== false;

  const newSeedCount = seedAutomationSuggestions.filter((s) =>
    isNewSeed(s.keyword),
  ).length;
  const repeatSeedCount = seedAutomationSuggestions.length - newSeedCount;

  const applyReviewedSeeds = () => {
    if (seedAutomationSuggestions.length === 0) {
      toast.warning('No keyword ideas to use yet. Click Find keyword ideas first.');
      return;
    }
    const chosen = newSeedsOnly
      ? seedAutomationSuggestions.filter((s) => isNewSeed(s.keyword))
      : seedAutomationSuggestions;

    if (chosen.length === 0) {
      // Never hand back an empty box without saying why.
      toast.warning(
        'Every keyword idea has been researched for this account before. ' +
          'Turn off "New ideas only" to use them anyway.',
      );
      return;
    }

    // ⛔ MERGE, NEVER REPLACE. This used to overwrite the manual seed box
    // outright. A real run on mikehammermoving.com lost all 12 hand-written
    // seeds to 7 LLM-invented phrases ("fast reliable senior moving company
    // missouri"), which Planner could barely price — the run collapsed from
    // ~110 keywords to 4. The operator's own seeds are the highest-signal
    // input we get; a button called "use" must not delete them.
    const manual = splitLines(seedKeywords);
    const seen = new Set(manual.map((k) => k.trim().toLowerCase()));
    const added = chosen
      .map((suggestion) => suggestion.keyword)
      .filter((keyword) => {
        const key = keyword.trim().toLowerCase();
        if (!key || seen.has(key)) return false;
        seen.add(key);
        return true;
      });

    setSeedKeywords([...manual, ...added].join('\n'));
    setSeedReviewApplied(true);

    const heldBack = newSeedsOnly && repeatSeedCount > 0
      ? `; left out ${repeatSeedCount} already researched`
      : '';
    toast.success(
      manual.length > 0
        ? `Added ${added.length} keyword idea${added.length === 1 ? '' : 's'} ` +
            `to your ${manual.length} keyword${manual.length === 1 ? '' : 's'}${heldBack}`
        : `Added ${added.length} keyword idea${added.length === 1 ? '' : 's'} to your keywords${heldBack}`,
    );
  };

  const applySuggestedPages = () => {
    if (sitePageSuggestions.length === 0) {
      toast.warning('No suggested pages yet. Click Suggest pages first.');
      return;
    }
    const current = splitLines(pages);
    const seen = new Set(current.map((url) => normalizeUrl(url)));
    const next = [...current];
    for (const suggestion of sitePageSuggestions) {
      const key = normalizeUrl(suggestion.url);
      if (!key || seen.has(key)) {
        continue;
      }
      seen.add(key);
      next.push(suggestion.url);
    }
    setPages(next.join('\n'));
    setSitePageReviewApplied(true);
    toast.success('Suggested pages added');
  };

  const research = useMutation({
    onMutate: () => {
      setResearchRunId(null);
    },
    mutationFn: () =>
      previewAdCampaignKeywords({
        tenant_id: tenantId,
        company_information: companyInfo.trim(),
        existing_target_keywords: splitLines(seedKeywords),
        seed_keyword_mode: supervisedSeeds ? 'manual_only' : 'manual_plus_workspace',
        pages: splitLines(pages),
        location_names: locationSeedNames,
        language: language || undefined,
        geo_target_constants: locations.map((l) => l.resource_name),
        target_cluster_count: targetClusterCount
          ? Number(targetClusterCount)
          : undefined,
      }),
    onSuccess: (result) => {
      const savedRunId = result.run_id ?? null;
      setResearchRunId(savedRunId);
      setClusters(result.clusters);
      setNegatives(result.negative_keywords ?? []);
      setQualityChecks(result.quality_checks ?? []);
      setTargetKeywordSuggestions(result.target_keyword_suggestions ?? []);
      setPlannerLanguage(result.language ?? null);
      setPlannerGeoTargetConstants(result.geo_target_constants ?? []);
      setPlannerSiteFilterUrl(result.site_filter_url ?? null);
      const sponsoredAdNoteValue =
        sponsoredAdQueryState.data?.sponsored_results?.length
          ? buildSponsoredAdNote(sponsoredAdQueryState.data)
          : null;
      setSponsoredAdNote(sponsoredAdNoteValue);
      if (result.clusters.length > 0) {
        const savedAt = new Date().toISOString();
        writeKeywordResearchResultCache({
          version: KEYWORD_RESEARCH_RESULT_CACHE_VERSION,
          tenant_id: tenantId,
          saved_at: savedAt,
          inputs: {
            company_information: companyInfo,
            pages,
            seed_keywords: seedKeywords,
            supervised_seeds: supervisedSeeds,
            language,
            locations,
          },
          result: {
            run_id: savedRunId,
            clusters: result.clusters,
            negative_keywords: result.negative_keywords ?? [],
            quality_checks: result.quality_checks ?? [],
            target_keyword_suggestions:
              result.target_keyword_suggestions ?? [],
            planner_language: result.language ?? null,
            planner_geo_target_constants: result.geo_target_constants ?? [],
            planner_site_filter_url: result.site_filter_url ?? null,
            sponsored_ad_note: sponsoredAdNoteValue,
          },
        });
        setCachedResultSavedAt(savedAt);
      } else {
        clearKeywordResearchResultCache(tenantId);
        setCachedResultSavedAt(null);
      }
      if (savedRunId) {
        queryClient.removeQueries({
          queryKey: keywordResearchHistoryQueryKeys.lists(),
        });
        void queryClient.invalidateQueries({
          queryKey: keywordResearchHistoryQueryKeys.lists(),
        });
        queryClient.removeQueries({
          queryKey: keywordResearchHistoryQueryKeys.detail(savedRunId),
        });
        void queryClient.invalidateQueries({
          queryKey: keywordResearchHistoryQueryKeys.detail(savedRunId),
        });
      }
      if (result.clusters.length === 0) {
        toast.warning(
          'No keyword groups found. Try a broader business description or add more keywords.',
        );
      } else if (!savedRunId) {
        toast.warning(
          `${result.clusters.length} keyword group${result.clusters.length !== 1 ? 's' : ''} found, but this run was not saved to History. Run it again before sending it to the Campaign Builder.`,
        );
      } else {
        const negCount = result.negative_keywords?.length ?? 0;
        toast.success(
          `${result.clusters.length} keyword group${result.clusters.length !== 1 ? 's' : ''} found and saved to History` +
            (negCount > 0 ? ` · ${negCount} suggested negative keyword${negCount !== 1 ? 's' : ''}` : ''),
        );
      }
    },
    onError: (err: unknown) => {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Research failed: ${detail}`);
    },
  });

  const canResearch = Boolean(tenantId) && !research.isPending && (
    !supervisedSeeds || seedReviewApplied
  );

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Megaphone className="h-4 w-4 text-coral" />
            Ad campaign keyword research
          </CardTitle>
          <CardDescription>
            Finds keywords for a Google Ads campaign and groups them into ad
            groups, with search volume and cost per click from Google. It is
            the same research the Campaign Builder runs, so you can check the
            ad groups before you build a campaign.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-1">
            <div className="flex items-center justify-between">
              <Label className="text-xs font-medium text-muted-foreground">
                Company information
              </Label>
              <div className="flex items-center gap-1">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 text-xs text-muted-foreground hover:bg-warm-bg"
                  disabled={
                    !tenantId ||
                    !companyInfo.trim() ||
                    saveMutation.isPending ||
                    autoFillMutation.isPending
                  }
                  onClick={() => saveMutation.mutate()}
                  title="Saves this text to the Brand Bible so it is still here next time."
                >
                  {saveMutation.isPending ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Saving…
                    </>
                  ) : (
                    <>Save</>
                  )}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="h-7 gap-1 text-xs text-teal-dark hover:bg-teal-light hover:text-teal-dark"
                  disabled={!tenantId || autoFillMutation.isPending}
                  onClick={() => autoFillMutation.mutate()}
                  title="Writes a 2 to 3 sentence description from the Brand Bible, the website's pages and the home page."
                >
                  {autoFillMutation.isPending ? (
                    <>
                      <Loader2 className="h-3 w-3 animate-spin" />
                      Researching…
                    </>
                  ) : (
                    <>
                      <Sparkles className="h-3 w-3" />
                      Write brief from research
                    </>
                  )}
                </Button>
              </div>
            </div>
            <Textarea
              rows={3}
              value={companyInfo}
              onChange={(e) => setCompanyInfo(e.target.value)}
              placeholder="Describe the business, services, customers, and market..."
            />
            <p className="text-[11px] text-muted-foreground">
              Optional. Leave it blank and we will write one from the workspace
              services and the website.
              {autoFillMutation.data?.sources_used && autoFillMutation.data.sources_used.length > 0 && (
                <span className="ml-2 text-teal-dark">
                  · from: {autoFillMutation.data.sources_used.join(', ').replace(/_/g, ' ')}
                </span>
              )}
              {briefGeneratedAt && (
                <span className="ml-2 text-muted-foreground">
                  · saved {formatRelativeTime(briefGeneratedAt)}
                </span>
              )}
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="space-y-1">
              <Label
                htmlFor="keyword-research-language"
                className="text-xs font-medium text-muted-foreground"
              >
                Language
              </Label>
              {/* Comms 9/23: the choice on the left, what it means on the
                  right; stacks on a phone. */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-3">
              <div className="w-full sm:w-56 sm:shrink-0">
              <Select value={language || 'auto'} onValueChange={(v) => setLanguage(v === 'auto' ? '' : v)}>
                <SelectTrigger id="keyword-research-language">
                  <SelectValue placeholder="Auto (workspace default)" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">Auto (workspace default)</SelectItem>
                  {LANGUAGES.map((lang) => (
                    <SelectItem key={lang.value} value={lang.value}>
                      {lang.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              </div>
              <p className="min-w-0 text-[11px] text-muted-foreground">
                The language people search in. Auto uses the workspace's
                country settings.
              </p>
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="target-cluster-count">Preferred ad groups</Label>
              <Input
                id="target-cluster-count"
                type="number"
                min={1}
                max={20}
                inputMode="numeric"
                placeholder="Auto"
                value={targetClusterCount}
                onChange={(e) => setTargetClusterCount(e.target.value)}
                className="w-32"
              />
              {/*
                ⚠ WORDED AS A TARGET ON PURPOSE. Ad groups come from the
                keywords that survive the pipeline, and one ad group must be
                targetable by a single responsive search ad. Asking for 8 from a
                keyword set that supports 2 would produce ad groups no ad can
                serve. The number chooses GRANULARITY where the keywords allow
                either shape; it cannot manufacture demand that is not there.
              */}
              <p className="text-[11px] text-muted-foreground">
                Optional target for how finely keywords are grouped. Leave blank
                to let the pipeline decide. Fewer groups come back if the
                keywords do not support the number — a group has to be
                targetable by one ad.
              </p>
            </div>
            <div className="space-y-1">
              {/* CORE-K-05: the picker's empty-state hint renders each
                  entry of `fallbackLocations` as a read-only chip so
                  the operator can SEE which workspace defaults the
                  research will target when nothing is picked here —
                  matching `buildLocationSeedNames` below, which is
                  the actual downstream fallback. Sponsored-ads picker
                  further down does NOT get this prop because it
                  hard-defaults to US and never consults
                  workspaceLocations. */}
              <LocationPicker
                tenantId={tenantId}
                selected={locations}
                onChange={setLocations}
                fallbackLocations={workspaceLocations}
                editableNames={editableWorkspaceLocations}
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="rounded border border-border bg-warm-bg p-3">
              <div>
                <Label className="text-xs font-medium text-muted-foreground">
                  Keyword ideas
                </Label>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Starting points for the research, suggested from the
                  workspace services and website. Use them, add your own, or
                  both.
                </p>
              </div>

              <div className="mt-3 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="bg-card"
                    disabled={!tenantId || seedPreviewMutation.isPending}
                    onClick={() => seedPreviewMutation.mutate({ silent: false })}
                  >
                    {seedPreviewMutation.isPending ? (
                      <>
                        <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
                        Finding keyword ideas…
                      </>
                    ) : (
                      <>
                        <Search className="mr-2 h-3.5 w-3.5" />
                        Find keyword ideas
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="bg-card"
                    disabled={seedAutomationSuggestions.length === 0}
                    onClick={applyReviewedSeeds}
                  >
                    Use these keyword ideas
                  </Button>
                  {/* Staging: leave out ideas already researched for this
                      workspace. Always shown, disabled until there is
                      something to count — an option nobody can find is an
                      option nobody has. */}
                  <label className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-coral"
                      checked={newSeedsOnly}
                      disabled={seedAutomationSuggestions.length === 0}
                      onChange={(event) => setNewSeedsOnly(event.target.checked)}
                    />
                    New ideas only
                    <span className="text-[10px]">
                      {seedAutomationSuggestions.length === 0
                        ? '(no ideas yet)'
                        : `(${newSeedCount} new${
                            repeatSeedCount > 0
                              ? `, ${repeatSeedCount} researched before`
                              : ''
                          })`}
                    </span>
                  </label>
                  {seedReviewApplied && (
                    <Badge variant="outline" className="bg-card text-[10px]">
                      in use
                    </Badge>
                  )}
                </div>
                {/* Comms 9/23: suggested keywords show without extra clicks.
                    They load on their own (see the auto-load effect above);
                    the internal step log is no longer shown. */}
                <SuggestedSeedsPanel
                  suggestions={seedAutomationSuggestions}
                  isLoading={seedPreviewMutation.isPending}
                  isError={seedPreviewMutation.isError}
                  priorUse={seedPriorUse}
                />
                <div className="flex items-start gap-2">
                  <Switch
                    id="keyword-ideas-approved-only"
                    checked={supervisedSeeds}
                    onCheckedChange={(checked) => {
                      setSupervisedSeeds(Boolean(checked));
                      setSeedReviewApplied(false);
                    }}
                  />
                  <div className="space-y-0.5">
                    <Label
                      htmlFor="keyword-ideas-approved-only"
                      className="cursor-pointer text-xs font-medium"
                    >
                      Only use the keywords I pick
                    </Label>
                    <p className="text-[11px] text-muted-foreground">
                      When on, the research uses only the ideas you add and
                      the keywords you type below. When off, we also add
                      keywords from the workspace.
                    </p>
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs font-medium text-muted-foreground">
                    Your own keywords (optional)
                  </Label>
                  <Textarea
                    rows={3}
                    value={seedKeywords}
                    onChange={(e) => setSeedKeywords(e.target.value)}
                    placeholder={seedKeywordPlaceholder}
                  />
                  <p className="text-[11px] text-muted-foreground">
                    One per line.
                    {supervisedSeeds
                      ? ' Only these keywords are used.'
                      : ' We also add keywords from Brand Bible services, Agile Plan priorities, service pages, your locations and the website.'}
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-1">
              <div className="flex items-center justify-between gap-2">
                <Label className="text-xs font-medium text-muted-foreground">
                  Site pages (optional)
                </Label>
                <div className="flex items-center gap-1">
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1 text-xs text-teal-dark hover:bg-teal-light hover:text-teal-dark"
                    disabled={!tenantId || sitePagePreviewMutation.isPending}
                    onClick={() => sitePagePreviewMutation.mutate()}
                    title="Suggests landing pages from the workspace settings, service pages and pages Google has indexed."
                  >
                    {sitePagePreviewMutation.isPending ? (
                      <>
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Finding…
                      </>
                    ) : (
                      <>
                        <Sparkles className="h-3 w-3" />
                        Suggest pages
                      </>
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="h-7 gap-1 text-xs text-muted-foreground hover:bg-warm-bg"
                    disabled={sitePageSuggestions.length === 0}
                    onClick={applySuggestedPages}
                  >
                    Use suggested
                  </Button>
                </div>
              </div>
              <Textarea
                rows={3}
                value={pages}
                onChange={(e) => {
                  setPages(e.target.value);
                  setSitePageReviewApplied(false);
                }}
                placeholder={pagePlaceholder}
              />
              <p className="text-[11px] text-muted-foreground">
                One URL per line. Google uses these pages to find keywords, and
                each ad group is sent to the best matching page.
                {sitePageReviewApplied && (
                  <span className="ml-2 text-teal-dark">· suggested pages added</span>
                )}
              </p>
              {sitePageSuggestions.length > 0 && (
                <SitePageSuggestionReviewPanel suggestions={sitePageSuggestions} />
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={() => research.mutate()}
              disabled={!canResearch}
              className="bg-coral hover:bg-coral-hover"
            >
              {research.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Researching…
                </>
              ) : (
                <>
                  <Sparkles className="mr-2 h-4 w-4" />
                  Run keyword research
                </>
              )}
            </Button>
            {!companyInfo.trim() && (
              <span className="text-xs text-muted-foreground">
                We will use the workspace details to describe the business and pick starting keywords.
              </span>
            )}
            {supervisedSeeds && !seedReviewApplied && (
              <span className="text-xs text-muted-foreground">
                Click Use these keyword ideas first, or turn off Only use the keywords I pick.
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {brandDefense?.competitors_bidding && (
        <BrandDefenseBanner data={brandDefense} />
      )}

      {clusters.length > 0 && cachedResultSavedAt && !research.isPending && (
        <div className="flex flex-wrap items-center gap-2 rounded border border-teal/30 bg-teal-light/20 px-3 py-2 text-sm">
          <CheckCircle2 className="h-4 w-4 text-teal-dark" />
          <span className="font-medium text-teal-dark">
            Keyword research cached
          </span>
          <span className="text-xs text-muted-foreground">
            Restores when you leave this page and come back. Last saved{' '}
            {new Date(cachedResultSavedAt).toLocaleString([], {
              month: 'short',
              day: 'numeric',
              hour: 'numeric',
              minute: '2-digit',
            })}.
          </span>
        </div>
      )}

      {clusters.length > 0 && hideClusterResults && !research.isPending && (
        <Card className="border-teal/30 bg-teal-light/10">
          <CardContent className="flex flex-wrap items-center gap-3 p-4">
            <CheckCircle2 className="h-4 w-4 text-teal-dark" />
            <div className="mr-auto">
              <div className="text-sm font-medium">Ad group clusters are ready</div>
              <div className="text-xs text-muted-foreground">
                Review keyword selection, landing pages, and campaign handoff in the cluster tab.
              </div>
            </div>
            <Button
              type="button"
              size="sm"
              className="bg-teal text-white hover:bg-teal-dark"
              onClick={() =>
                navigate('/ads-tools/keyword-research?platform=google-ads&research_tab=clusters')
              }
            >
              Open clusters
              <ArrowRight className="ml-1 h-3.5 w-3.5" />
            </Button>
          </CardContent>
        </Card>
      )}

      {clusters.length > 0 && !hideClusterResults && (
        <ClustersResult
          clusters={clusters}
          negatives={negatives}
          qualityChecks={qualityChecks}
          targetKeywordSuggestions={targetKeywordSuggestions}
          plannerLanguage={plannerLanguage}
          plannerGeoTargetConstants={plannerGeoTargetConstants}
          plannerSiteFilterUrl={plannerSiteFilterUrl}
          selectedLocations={locations}
          researchRunId={researchRunId}
        />
      )}

      {research.isPending && clusters.length === 0 && (
        <div className="flex items-center gap-2 rounded border border-border bg-warm-bg p-4 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Researching keywords in 6 steps (brainstorm → enrich → filter → cluster →
          assign). Usually takes 60-90 seconds.
        </div>
      )}
    </div>
  );
}

export function SponsoredAdsLookupPanel() {
  const tenantId = useTenantStore((s) => s.currentTenant ?? '');
  const [sponsoredAdQuery, setSponsoredAdQuery] = useState('seo company');
  // Geo target via the same picker as keyword research — any location
  // (country/region/city/DMA). The first selection drives the lookup.
  const [sponsoredLocations, setSponsoredLocations] = useState<GeoTargetSuggestion[]>([]);
  const [sponsoredAdNote, setSponsoredAdNote] = useState<string | null>(null);
  const [showSponsoredAds, setShowSponsoredAds] = useState(false);

  const geo = sponsoredLocations[0] ?? null;
  // `gl` (country) drives DataForSEO; `uule` (from the canonical name) gives
  // the Google link sub-country precision (e.g. Utah). Defaults to US.
  const sponsoredGl = (geo?.country_code || 'us').toLowerCase();
  const sponsoredUule = geo ? encodeUule(geo.canonical_name) : '';
  const marketLabel = geo ? geo.name : 'United States (default)';

  const sponsoredAdQueryState = useQuery({
    queryKey: ['sponsored-ads', tenantId, sponsoredAdQuery, sponsoredGl],
    queryFn: () => fetchSponsoredAdProbe(sponsoredAdQuery.trim(), sponsoredGl),
    enabled: false,
    staleTime: 10 * 60 * 1000,
  });

  // Client-side ('bookmarklet') capture: the operator scrapes the live ad
  // block in their own Google session; the bookmarklet reopens THIS page with
  // the ads in the URL fragment, which we read on load (no polling, no fetch).
  const [hashCapture, setHashCapture] = useState<BookmarkletCaptureResult | null>(
    null,
  );
  // Reopen the current Sponsored Ads view with the capture appended.
  const bookmarkletHref = useMemo(() => {
    if (typeof window === 'undefined') return '';
    const appUrl =
      window.location.origin + window.location.pathname + window.location.search;
    return buildSponsoredAdBookmarklet(appUrl);
  }, []);

  // Read a #sac= capture the bookmarklet appended, then clear it so a refresh
  // doesn't re-trigger. Also persists it to the backend (best-effort).
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const parsed = parseSponsoredAdHash(window.location.hash);
    if (!parsed) return;
    setHashCapture({
      enabled: true,
      captured: true,
      keyword: parsed.keyword || null,
      gl: parsed.gl || null,
      sponsored_results: parsed.sponsored_results as never,
    });
    // A capture arrived via the bookmarklet — auto-expand so it's visible
    // without the operator having to click "Show sponsored ads".
    setShowSponsoredAds(true);
    window.history.replaceState(
      null,
      '',
      window.location.pathname + window.location.search,
    );
    if (tenantId) {
      ingestBookmarkletCapture(tenantId, parsed).catch(() => {
        /* persistence is best-effort; display already works from the hash */
      });
    }
  }, [tenantId]);

  useEffect(() => {
    const data = sponsoredAdQueryState.data;
    if (!data?.sponsored_results?.length) return;
    const note = buildSponsoredAdNote(data);
    setSponsoredAdNote(note);
    const cache = readKeywordResearchResultCache(tenantId);
    if (!cache) return;
    if (cache.result.sponsored_ad_note === note) return;
    writeKeywordResearchResultCache({
      ...cache,
      result: {
        ...cache.result,
        sponsored_ad_note: note,
      },
    });
  }, [tenantId, sponsoredAdQueryState.data]);

  return (
    <Card className="border-coral/20 bg-warm-bg/70">
      <CardHeader className="pb-3">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div>
            <CardTitle className="flex items-center gap-2 text-base">
              <Search className="h-4 w-4 text-coral" />
              Sponsored ads lookup
            </CardTitle>
            <CardDescription>
              Pull the sponsored block for a query in a chosen target market —
              ads are geo-targeted, so the market decides which advertisers
              appear. Uses the provider-backed sponsored-ad capture path, not
              SerpAPI.
            </CardDescription>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            onClick={() => setShowSponsoredAds((value) => !value)}
          >
            {showSponsoredAds ? 'Hide sponsored ads' : 'Show sponsored ads'}
          </Button>
        </div>
      </CardHeader>
      {showSponsoredAds ? (
        <CardContent className="space-y-3">
          <div className="flex flex-col gap-3 md:flex-row md:items-end">
            <div className="flex-1 space-y-1">
              <Label htmlFor="sponsored-ad-query">Query</Label>
              <Input
                id="sponsored-ad-query"
                value={sponsoredAdQuery}
                onChange={(e) => setSponsoredAdQuery(e.target.value)}
                placeholder="seo company"
              />
            </div>
            <div className="min-w-[16rem] flex-1 space-y-1">
              <LocationPicker
                tenantId={tenantId}
                selected={sponsoredLocations}
                onChange={setSponsoredLocations}
              />
              <p className="text-[11px] text-muted-foreground">
                Target market — type any location (country, state like
                <span className="font-medium"> Utah</span>, city, or DMA). The
                first one is used. Defaults to US.
              </p>
            </div>
            <Button
              type="button"
              className="bg-coral hover:bg-coral-hover"
              disabled={!sponsoredAdQuery.trim() || sponsoredAdQueryState.isFetching}
              onClick={() => sponsoredAdQueryState.refetch()}
            >
              {sponsoredAdQueryState.isFetching ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Fetching…
                </>
              ) : (
                <>
                  <Search className="mr-2 h-4 w-4" />
                  Fetch sponsored ads
                </>
              )}
            </Button>
          </div>
          {sponsoredAdQueryState.data?.skipped_reason ? (
            <div className="rounded border border-coral/30 bg-card px-3 py-2 text-xs text-coral-dark">
              Sponsored lookup unavailable: {sponsoredAdQueryState.data.skipped_reason === 'flag_disabled'
                ? 'the sponsored-ad feature flag is off'
                : sponsoredAdQueryState.data.skipped_reason}
            </div>
          ) : null}
          {sponsoredAdQueryState.data?.sponsored_results?.length ? (
            <div className="space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-card"
                  onClick={async () => {
                    await copySponsoredAdBlock(sponsoredAdQueryState.data!);
                    toast.success('Sponsored block copied');
                  }}
                >
                  <Copy className="mr-2 h-3.5 w-3.5" />
                  Copy sponsor block
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-card"
                  disabled={!sponsoredAdNote}
                  onClick={async () => {
                    if (!sponsoredAdNote) return;
                    if (!navigator.clipboard?.writeText) {
                      throw new Error('Clipboard API unavailable');
                    }
                    await navigator.clipboard.writeText(sponsoredAdNote);
                    toast.success('Sponsor note copied');
                  }}
                >
                  <Copy className="mr-2 h-3.5 w-3.5" />
                  Copy note
                </Button>
                {sponsoredAdNote ? (
                  <span className="text-xs text-muted-foreground">
                    Saved note: {sponsoredAdNote}
                  </span>
                ) : null}
              </div>
              {sponsoredAdQueryState.data.sponsored_results.map((ad) => (
                <div
                  key={`${ad.domain ?? ad.title}-${ad.position ?? 0}`}
                  className="rounded border border-border bg-card p-3"
                >
                  <div className="flex flex-wrap items-center gap-2">
                    <Badge variant="outline" className="border-coral/30 bg-coral-light text-coral-dark">
                      Sponsored
                    </Badge>
                    <div className="font-medium text-foreground">{ad.title}</div>
                  </div>
                  <div className="mt-1 text-xs text-muted-foreground">
                    {ad.display_url || ad.domain || ad.url || '—'}
                  </div>
                  {ad.snippet ? (
                    <div className="mt-2 text-sm text-muted-foreground">{ad.snippet}</div>
                  ) : null}
                  {ad.sitelinks.length ? (
                    <div className="mt-2 flex flex-wrap gap-2 text-xs">
                      {ad.sitelinks.map((label) => (
                        <Badge key={label} variant="secondary" className="bg-warm-bg">
                          {label}
                        </Badge>
                      ))}
                    </div>
                  ) : null}
                </div>
              ))}
              <SponsoredAdAnalysisPanel
                tenantId={tenantId}
                query={sponsoredAdQuery.trim()}
                gl={sponsoredGl}
                ads={sponsoredAdQueryState.data.sponsored_results}
              />
            </div>
          ) : sponsoredAdQueryState.data && !sponsoredAdQueryState.data.skipped_reason ? (
            <div className="rounded border border-dashed border-border bg-card px-3 py-2 text-sm text-muted-foreground">
              No sponsored results returned for this query in{' '}
              {sponsoredAdQueryState.data.gl
                ? sponsoredAdQueryState.data.gl.toUpperCase()
                : marketLabel}
              . Ads are geo-targeted — a block that shows in one market may not
              exist in another, so try a different target market.
            </div>
          ) : null}

          <SponsoredAdBookmarkletSection
            href={bookmarkletHref}
            googleUrl={buildGoogleSearchUrl(
              sponsoredAdQuery.trim() || 'marketing company',
              sponsoredGl,
              'en',
              sponsoredUule,
            )}
            marketLabel={marketLabel}
            capture={hashCapture}
          />
          {hashCapture?.captured && hashCapture.sponsored_results.length ? (
            <SponsoredAdAnalysisPanel
              tenantId={tenantId}
              query={hashCapture.keyword || sponsoredAdQuery.trim()}
              gl={hashCapture.gl || sponsoredGl}
              ads={hashCapture.sponsored_results}
            />
          ) : null}
        </CardContent>
      ) : null}
    </Card>
  );
}

function SponsoredAdCard({ ad }: { ad: SponsoredAdSeen }) {
  return (
    <div className="rounded border border-border bg-card p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="outline" className="border-coral/30 bg-coral-light text-coral-dark">
          Sponsored
        </Badge>
        <div className="font-medium text-foreground">{ad.title}</div>
      </div>
      {ad.advertiser ? (
        <div className="mt-1 text-sm font-medium text-teal-dark">{ad.advertiser}</div>
      ) : null}
      <div className="mt-0.5 text-xs text-muted-foreground">
        {ad.display_url || ad.domain || ad.url || '—'}
      </div>
      {ad.snippet ? (
        <div className="mt-2 text-sm text-muted-foreground">{ad.snippet}</div>
      ) : null}
      {ad.sitelinks.length ? (
        <div className="mt-2 flex flex-wrap gap-2 text-xs">
          {ad.sitelinks.map((label) => (
            <Badge key={label} variant="secondary" className="bg-warm-bg">
              {label}
            </Badge>
          ))}
        </div>
      ) : null}
    </div>
  );
}

const PRIORITY_STYLES: Record<string, string> = {
  high: 'border-coral/40 bg-coral-light text-coral-dark',
  medium: 'border-teal/40 bg-teal-light text-teal-dark',
  low: 'border-border bg-warm-bg text-muted-foreground',
};

/** "Reverse-engineer these ads" — sends the captured sponsored block to the
 * analyzer and renders why each advertiser wins + recommendations to apply. */
function SponsoredAdAnalysisPanel({
  tenantId,
  query,
  gl,
  ads,
}: {
  tenantId: string;
  query: string;
  gl: string;
  ads: SponsoredAdSeen[];
}) {
  const [analysis, setAnalysis] = useState<SponsoredAdAnalysis | null>(null);
  const mutation = useMutation({
    mutationFn: () => analyzeSponsoredAds({ tenantId, query, gl, ads }),
    onSuccess: (data) => {
      setAnalysis(data);
      if (data.skipped_reason) {
        toast.error(`Analysis unavailable: ${data.skipped_reason}`);
      }
    },
    onError: () => toast.error('Could not analyze these ads.'),
  });

  if (!ads.length) return null;
  const result = analysis && !analysis.skipped_reason ? analysis : null;

  return (
    <div className="mt-3 space-y-2 rounded border border-teal/30 bg-card p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="text-sm font-medium text-teal-dark">
          Reverse-engineer these ads
        </div>
        <Button
          type="button"
          size="sm"
          className="bg-coral hover:bg-coral-hover"
          disabled={mutation.isPending}
          onClick={() => mutation.mutate()}
        >
          {mutation.isPending ? (
            <>
              <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin" />
              Analyzing…
            </>
          ) : (
            <>
              <Sparkles className="mr-2 h-3.5 w-3.5" />
              Analyze why they rank
            </>
          )}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Tears down each advertiser's ad relevance, copy, extensions, and landing
        page to explain why they win the paid placement — then recommends what to
        apply to your own pages &amp; campaigns.
      </p>

      {result ? (
        <div className="space-y-3 pt-1">
          {result.patterns.length ? (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                Shared patterns
              </div>
              <ul className="mt-1 list-disc space-y-0.5 pl-4 text-sm text-foreground">
                {result.patterns.map((p, i) => (
                  <li key={i}>{p}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {result.recommendations.length ? (
            <div>
              <div className="text-xs font-semibold uppercase text-muted-foreground">
                Apply to your pages &amp; campaigns
              </div>
              <div className="mt-1 space-y-2">
                {result.recommendations.map((r, i) => (
                  <div key={i} className="rounded border border-border bg-warm-bg/50 p-2">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge
                        variant="outline"
                        className={PRIORITY_STYLES[r.priority] ?? PRIORITY_STYLES.medium}
                      >
                        {r.priority}
                      </Badge>
                      <span className="text-xs font-medium text-muted-foreground">
                        {r.area}
                      </span>
                    </div>
                    <div className="mt-1 text-sm text-foreground">{r.action}</div>
                    {r.rationale ? (
                      <div className="mt-0.5 text-xs text-muted-foreground">{r.rationale}</div>
                    ) : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {result.per_ad.length ? (
            <details className="rounded border border-border bg-warm-bg/40 p-2">
              <summary className="cursor-pointer text-xs font-semibold uppercase text-muted-foreground">
                Per-advertiser teardown ({result.per_ad.length})
              </summary>
              <div className="mt-2 space-y-2">
                {result.per_ad.map((a, i) => (
                  <div key={i} className="rounded border border-border bg-card p-2 text-xs">
                    <div className="text-sm font-medium text-teal-dark">
                      {a.advertiser || a.domain}
                    </div>
                    {a.why_it_ranks ? (
                      <div className="mt-0.5 text-muted-foreground">{a.why_it_ranks}</div>
                    ) : null}
                    <dl className="mt-1 space-y-1">
                      {a.ad_rank_factors.keyword_relevance ? (
                        <div>
                          <dt className="inline font-medium text-foreground">Relevance: </dt>
                          <dd className="inline text-muted-foreground">{a.ad_rank_factors.keyword_relevance}</dd>
                        </div>
                      ) : null}
                      {a.ad_rank_factors.ad_copy_tactics.length ? (
                        <div>
                          <dt className="inline font-medium text-foreground">Copy: </dt>
                          <dd className="inline text-muted-foreground">{a.ad_rank_factors.ad_copy_tactics.join(' · ')}</dd>
                        </div>
                      ) : null}
                      {a.ad_rank_factors.extensions ? (
                        <div>
                          <dt className="inline font-medium text-foreground">Extensions: </dt>
                          <dd className="inline text-muted-foreground">{a.ad_rank_factors.extensions}</dd>
                        </div>
                      ) : null}
                      {a.ad_rank_factors.landing_page_experience ? (
                        <div>
                          <dt className="inline font-medium text-foreground">Landing page: </dt>
                          <dd className="inline text-muted-foreground">{a.ad_rank_factors.landing_page_experience}</dd>
                        </div>
                      ) : null}
                    </dl>
                  </div>
                ))}
              </div>
            </details>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function SponsoredAdBookmarkletSection({
  href,
  googleUrl,
  marketLabel,
  capture,
}: {
  href: string;
  googleUrl: string;
  marketLabel: string;
  capture: BookmarkletCaptureResult | null;
}) {
  // React sanitizes `javascript:` URLs in href props (replaces them with a
  // throwing stub), which breaks a dragged bookmarklet. Set the attribute
  // directly via a ref so the real code lands in the bookmark.
  const linkRef = useRef<HTMLAnchorElement>(null);
  useEffect(() => {
    if (linkRef.current && href) linkRef.current.setAttribute('href', href);
  }, [href]);

  if (!href) return null;
  const captured = capture?.captured ? capture : null;
  return (
    <div className="mt-2 space-y-2 rounded border border-teal/30 bg-teal-light/40 p-3">
      <div className="text-sm font-medium text-teal-dark">
        Capture the exact ad block you see in Google
      </div>
      <p className="text-xs text-muted-foreground">
        Provider scrapers run from datacenter IPs and show a representative ad
        block, not your personalized one. To grab exactly what{' '}
        <em>you</em> see:
      </p>
      <ol className="ml-4 list-decimal space-y-0.5 text-xs text-muted-foreground">
        <li>
          Drag the button below to your bookmarks bar{' '}
          <span className="font-medium">(once)</span>.
        </li>
        <li>
          <a
            href={googleUrl}
            target="_blank"
            rel="noreferrer"
            className="font-medium text-teal-dark underline underline-offset-2"
          >
            Open this search in Google ({marketLabel})
          </a>{' '}
          — the query + market are geo-targeted into the URL for you.
        </li>
        <li>
          Click the bookmark on the results page. A new tab opens here with the
          ads you saw.
        </li>
      </ol>
      <div className="flex flex-wrap items-center gap-3">
        <a
          ref={linkRef}
          onClick={(e) => {
            e.preventDefault();
            toast.info(
              'Drag this button to your bookmarks bar, then click it on a Google results page.',
            );
          }}
          draggable
          className="inline-flex cursor-grab items-center gap-1 rounded bg-coral px-3 py-1.5 text-sm font-medium text-white"
          title="Drag me to your bookmarks bar"
        >
          <Megaphone className="h-3.5 w-3.5" />
          Capture Google Ads → AgileCo
        </a>
        <Button
          type="button"
          variant="outline"
          size="sm"
          className="bg-card"
          onClick={async () => {
            if (!navigator.clipboard?.writeText) {
              toast.error('Clipboard unavailable — drag the button instead.');
              return;
            }
            await navigator.clipboard.writeText(href);
            toast.success(
              'Bookmarklet code copied. Create a bookmark and paste it as the URL.',
            );
          }}
        >
          <Copy className="mr-2 h-3.5 w-3.5" />
          Copy bookmarklet code
        </Button>
      </div>
      {captured ? (
        <div className="space-y-2">
          <div className="text-xs font-medium text-teal-dark">
            Captured {captured.sponsored_results.length} ad(s) from your browser
            {captured.keyword ? ` for "${captured.keyword}"` : ''}
            {captured.gl ? ` (${captured.gl.toUpperCase()})` : ''}
          </div>
          {captured.sponsored_results.length ? (
            captured.sponsored_results.map((ad) => (
              <SponsoredAdCard
                key={`bm-${ad.domain ?? ad.title}-${ad.position ?? 0}`}
                ad={ad}
              />
            ))
          ) : (
            <div className="rounded border border-dashed border-border bg-card px-3 py-2 text-xs text-muted-foreground">
              The page had no sponsored block to capture (Google showed no ads
              for that search).
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}

/** Pick which research run's ad groups to look at.
 *
 * Runs are dated and every one is kept, so the operator can go back to what a
 * previous session produced instead of losing it to the newest run. The active
 * set is marked, because that is the one the campaign builder will use.
 */
function RunSelector({
  runs,
  selectedRunId,
  activeRunId,
  onSelect,
  loading,
}: {
  runs: KeywordResearchHistoryItem[];
  selectedRunId: string | null;
  activeRunId: string | null;
  onSelect: (runId: string | null) => void;
  loading: boolean;
}) {
  // ⛔ A BARE <select> IS NOT DISCOVERABLE. Shipped as a labelled dropdown and
  // the operator still asked "where are the previous clusters?" while looking
  // straight at it — twice in one session for two different additions. A
  // control that has to be opened before it reveals that history exists reads
  // as a caption, not as navigation.
  //
  // Previous runs are now VISIBLE without interaction: the recent ones sit on
  // the surface as buttons showing what each contains, so the operator can see
  // that the 118-keyword run still exists rather than having to go looking.
  const [expanded, setExpanded] = useState(false);
  if (runs.length === 0) return null;

  const RECENT = 5;
  const shown = expanded ? runs : runs.slice(0, RECENT);
  const hidden = runs.length - shown.length;

  const label = (run: KeywordResearchHistoryItem) =>
    `${new Date(run.created_at).toLocaleString([], {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })}`;

  return (
    <div className="rounded border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <History className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Previous research runs</span>
        <Badge variant="outline" className="text-[10px]">
          {runs.length} saved
        </Badge>
        {loading && (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        )}
        <span className="text-xs text-muted-foreground">
          Nothing is overwritten by a new run — pick any run to see its ad groups.
        </span>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {shown.map((run) => {
          const isSelected = run.id === selectedRunId;
          const isActive = run.id === activeRunId;
          return (
            <button
              key={run.id}
              type="button"
              onClick={() => onSelect(isActive ? null : run.id)}
              aria-pressed={isSelected}
              className={
                'flex flex-col items-start rounded border px-2.5 py-1.5 text-left text-xs transition-colors ' +
                (isSelected
                  ? 'border-coral bg-coral-light/40 text-coral-dark'
                  : 'border-border bg-warm-bg hover:border-coral/50')
              }
            >
              <span className="font-medium">
                {label(run)}
                {isActive && (
                  <span className="ml-1 text-[10px] font-normal text-teal-dark">
                    active
                  </span>
                )}
              </span>
              <span className="text-[10px] text-muted-foreground">
                {run.cluster_count} ad group{run.cluster_count === 1 ? '' : 's'}
                {' · '}
                {run.total_keywords} keyword{run.total_keywords === 1 ? '' : 's'}
              </span>
            </button>
          );
        })}

        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className="rounded border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:border-coral/50"
          >
            +{hidden} older
          </button>
        )}
        {expanded && runs.length > RECENT && (
          <button
            type="button"
            onClick={() => setExpanded(false)}
            className="rounded border border-dashed border-border px-2.5 py-1.5 text-xs text-muted-foreground hover:border-coral/50"
          >
            Show fewer
          </button>
        )}
      </div>

      {/* Kept for keyboard/screen-reader users and for very long histories. */}
      <label htmlFor="cluster-run-select" className="sr-only">
        Research run
      </label>
      <select
        id="cluster-run-select"
        className="mt-2 w-full rounded border border-input bg-background px-2 py-1 text-xs md:w-auto"
        value={selectedRunId ?? ''}
        onChange={(event) => onSelect(event.target.value || null)}
      >
        {runs.map((run) => (
          <option key={run.id} value={run.id}>
            {label(run)}
            {' — '}
            {run.cluster_count} ad group{run.cluster_count === 1 ? '' : 's'}
            {', '}
            {run.total_keywords} keyword{run.total_keywords === 1 ? '' : 's'}
            {run.id === activeRunId ? ' (active)' : ''}
          </option>
        ))}
      </select>
    </div>
  );
}

export function AdCampaignClusterResultsPanel({
  workspaceName,
}: Pick<AdCampaignKeywordsPanelProps, 'workspaceName'>) {
  const tenantId = useTenantStore((s) => s.currentTenant ?? '');
  const activeQuery = useActiveResearchRun(tenantId);
  const activeRun = activeQuery.data?.run ?? null;

  // ⛔ THIS TAB SHOWED EXACTLY ONE RUN AND NOTHING ELSE. Every research run a
  // tenant has ever done is stored and dated, but the only way in was whichever
  // one happened to be "active", so a new run appeared to replace the previous
  // ones and the operator reported "previous runs are lost". (They were also
  // genuinely failing to save until the persistence fix — two causes, one
  // symptom, which is why this looked like a storage bug rather than a
  // navigation one.)
  //
  // Ad groups are not versioned by date, so the run is a SELECTOR here, not a
  // parent grouping in the tree — picking a run swaps which set you are
  // looking at, and the active one stays marked as active.
  const [viewRunId, setViewRunId] = useState<string | null>(null);
  const historyQuery = useResearchHistory({ tenant_id: tenantId, limit: 25 });
  const runs = historyQuery.data?.runs ?? [];

  // null means "follow the active run", so a fresh run shows up without the
  // operator having to reselect it.
  const selectedRunId = viewRunId ?? activeRun?.id ?? null;
  const isViewingActive = !viewRunId || viewRunId === activeRun?.id;
  const otherRunQuery = useResearchRun(isViewingActive ? null : selectedRunId);
  const shownRun = isViewingActive ? activeRun : otherRunQuery.data ?? null;

  const activeClusters = useMemo(
    () =>
      (shownRun?.clusters ?? []).map((cluster) => ({
        ...cluster,
        keywords: (cluster.keywords ?? []).map((keyword) => ({
            ...keyword,
            keyword: keyword.keyword ?? '',
            cpc: typeof keyword.cpc === 'number' ? keyword.cpc : 0,
            volume: typeof keyword.volume === 'number' ? keyword.volume : 0,
          })),
      })),
    [shownRun],
  );

  if (activeQuery.isLoading) {
    return (
      <Card>
        <CardContent className="flex items-center gap-2 p-6 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading active ad group clusters...
        </CardContent>
      </Card>
    );
  }

  if (activeQuery.isError) {
    return (
      <Card>
        <CardContent className="p-6 text-sm text-destructive">
          Failed to load active clusters. {String((activeQuery.error as Error)?.message ?? '')}
        </CardContent>
      </Card>
    );
  }

  if (activeQuery.data?.migration_required) {
    return (
      <Card className="border-coral/40 bg-coral-light/10">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TriangleAlert className="h-4 w-4 text-coral-dark" />
            Active cluster storage needs migration
          </CardTitle>
          <CardDescription>
            {activeQuery.data.message
              ?? 'Run the active keyword cluster migration, then refresh this tab.'}
          </CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!shownRun || activeClusters.length === 0) {
    return (
      <div className="space-y-3">
        <RunSelector
          runs={runs}
          selectedRunId={selectedRunId}
          activeRunId={activeRun?.id ?? null}
          onSelect={setViewRunId}
          loading={historyQuery.isLoading || otherRunQuery.isLoading}
        />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-4 w-4 text-teal" />
              {runs.length > 0
                ? 'This run has no saved ad group clusters'
                : 'No saved ad group clusters'}
            </CardTitle>
            <CardDescription>
              {runs.length > 0
                ? 'Pick another run above, or run keyword research again.'
                : `Run keyword research for ${workspaceName || tenantId || 'this workspace'} to populate this tab.`}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <RunSelector
        runs={runs}
        selectedRunId={selectedRunId}
        activeRunId={activeRun?.id ?? null}
        onSelect={setViewRunId}
        loading={historyQuery.isLoading || otherRunQuery.isLoading}
      />
      <div className="flex flex-wrap items-center gap-2 rounded border border-teal/30 bg-teal-light/20 px-3 py-2 text-sm">
        <CheckCircle2 className="h-4 w-4 text-teal-dark" />
        <span className="font-medium text-teal-dark">
          {isViewingActive ? 'Active keyword cluster set' : 'Viewing an earlier run'}
        </span>
        <span className="text-xs text-muted-foreground">
          Run {shownRun.id.slice(0, 8)} · {new Date(shownRun.created_at).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}
          {isViewingActive ? '' : ' · not the active set'}
        </span>
      </div>
      <ClustersResult
        clusters={activeClusters}
        negatives={[]}
        qualityChecks={[]}
        targetKeywordSuggestions={[]}
        plannerLanguage={null}
        plannerGeoTargetConstants={[]}
        plannerSiteFilterUrl={null}
        selectedLocations={[]}
        researchRunId={shownRun.id}
        clustersOnly
      />
    </div>
  );
}

export function AdCampaignNegativeKeywordsPanel({
  workspaceName,
}: Pick<AdCampaignKeywordsPanelProps, 'workspaceName'>) {
  const tenantId = useTenantStore((s) => s.currentTenant ?? '');
  const [cache, setCache] = useState<KeywordResearchResultCache | null>(() =>
    readKeywordResearchResultCache(tenantId),
  );

  useEffect(() => {
    const refreshCache = () => {
      setCache(readKeywordResearchResultCache(tenantId));
    };

    refreshCache();
    window.addEventListener('focus', refreshCache);
    window.addEventListener('storage', refreshCache);

    return () => {
      window.removeEventListener('focus', refreshCache);
      window.removeEventListener('storage', refreshCache);
    };
  }, [tenantId]);

  const negatives = cache?.result.negative_keywords ?? [];

  if (!cache) {
    return (
      <div className="space-y-3">
        <SuggestedNegativesPanel negatives={[]} />
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="h-4 w-4 text-teal" />
              No keyword research result yet
            </CardTitle>
            <CardDescription>
              Run keyword research for {workspaceName || tenantId || 'this workspace'} to populate the negative keyword review list.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2 rounded border border-teal/30 bg-teal-light/20 px-3 py-2 text-sm">
        <CheckCircle2 className="h-4 w-4 text-teal-dark" />
        <span className="font-medium text-teal-dark">
          Last keyword research result
        </span>
        <span className="text-xs text-muted-foreground">
          {negatives.length} suggested negative keyword{negatives.length !== 1 ? 's' : ''} · saved{' '}
          {new Date(cache.saved_at).toLocaleString([], {
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
          })}.
        </span>
      </div>
      <SuggestedNegativesPanel negatives={negatives} />
    </div>
  );
}

function buildSeedKeywordPlaceholder({
  businessName,
  services,
  locations,
  tenantId,
}: {
  businessName?: string;
  services: string[];
  locations: string[];
  tenantId: string;
}): string {
  const primaryLocation = cleanLocation(locations[0] ?? '');
  const examples = services
    .map(cleanServiceName)
    .filter(Boolean)
    .slice(0, 2)
    .map((service) =>
      primaryLocation ? `${service} ${primaryLocation}` : service,
    );

  if (examples.length >= 2) {
    return examples.join('\n');
  }

  const label = cleanBusinessLabel(businessName || tenantId);
  if (examples.length === 1) {
    return [
      examples[0],
      primaryLocation
        ? `${label} consultation ${primaryLocation}`
        : `${label} consultation`,
    ].join('\n');
  }

  return [
    primaryLocation ? `${label} services ${primaryLocation}` : `${label} services`,
    primaryLocation ? `${label} consultation ${primaryLocation}` : `${label} consultation`,
  ].join('\n');
}


function buildPagePlaceholder({
  tenantId,
  pages,
}: {
  tenantId: string;
  pages: string[];
}): string {
  const indexedPages = pages
    .map((page) => page.trim())
    .filter(Boolean)
    .slice(0, 2);

  if (indexedPages.length > 0) {
    return indexedPages.join('\n');
  }

  const domain = workspaceDomain(tenantId);
  if (!domain) {
    return 'https://your-domain.com/services\nhttps://your-domain.com/contact';
  }

  return [`https://${domain}/services`, `https://${domain}/contact`].join('\n');
}


function cleanServiceName(service: string): string {
  return service
    .replace(/\s*\([^)]*\)\s*/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();
}


function cleanLocation(location: string): string {
  return location
    .replace(/,\s*united states$/i, '')
    .replace(/\s+/g, ' ')
    .trim();
}


function cleanBusinessLabel(value: string): string {
  const strippedDomain = value
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .replace(/\.[a-z]{2,}$/i, '')
    .replace(/[-_]+/g, ' ');
  return strippedDomain.trim() || 'workspace';
}


function workspaceDomain(tenantId: string): string {
  return tenantId
    .replace(/^https?:\/\//i, '')
    .replace(/^www\./i, '')
    .replace(/\/.*$/, '')
    .trim();
}


function languageLabel(value: string | null | undefined): string {
  if (!value) return 'Auto/default';
  return LANGUAGES.find((language) => language.value === value)?.label ?? value;
}


function geoTargetLabel(
  geoTargetConstants: string[],
  selectedLocations: GeoTargetSuggestion[],
): string {
  if (geoTargetConstants.length === 0) {
    return 'Worldwide/default';
  }
  const selectedByResource = new Map(
    selectedLocations.map((location) => [
      location.resource_name,
      location.canonical_name || location.name || location.resource_name,
    ]),
  );
  return geoTargetConstants
    .map((resourceName) => selectedByResource.get(resourceName) ?? resourceName)
    .join(', ');
}


function buildLocationSeedNames(
  selectedLocations: GeoTargetSuggestion[],
  workspaceLocations: string[],
): string[] {
  const selectedNames = selectedLocations
    .map((location) => location.canonical_name || location.name)
    .map((value) => value?.trim())
    .filter((value): value is string => Boolean(value));
  if (selectedNames.length > 0) {
    return selectedNames;
  }
  return workspaceLocations.map((value) => value.trim()).filter(Boolean);
}


function keywordHasCompetition(keyword: PreviewedKeyword): boolean {
  return Boolean(keyword.kp_competition);
}


function keywordHasBidRange(keyword: PreviewedKeyword): boolean {
  return (
    keyword.kp_low_top_of_page_bid_micros != null ||
    keyword.kp_high_top_of_page_bid_micros != null
  );
}


function keywordHasMonthlyTrend(keyword: PreviewedKeyword): boolean {
  return (keyword.kp_monthly_search_volumes?.length ?? 0) > 0;
}


type LowHangingFruitSignal = {
  status: 'yes' | 'review' | 'no';
  label: string;
  reason: string;
};

type KeywordOpportunityBenchmarks = {
  medianCpc: number;
  medianVolume: number;
};

const BUYER_INTENT_RE =
  /\b(agency|agencies|company|companies|service|services|provider|providers|firm|firms|consultant|consulting|hire|quote|demo|near me)\b/i;

function keywordSelectionKey(keyword: string): string {
  return keyword.toLowerCase().replace(/\s+/g, ' ').trim();
}

function median(values: number[]): number {
  const sorted = values
    .filter((value) => Number.isFinite(value) && value > 0)
    .sort((a, b) => a - b);
  if (sorted.length === 0) return 0;
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 0
    ? (sorted[mid - 1] + sorted[mid]) / 2
    : sorted[mid];
}

function buildKeywordOpportunityBenchmarks(
  clusters: PreviewedCluster[],
): KeywordOpportunityBenchmarks {
  const keywords = clusters.flatMap((cluster) => cluster.keywords);
  return {
    medianCpc: median(keywords.map((keyword) => keyword.cpc)),
    medianVolume: median(keywords.map((keyword) => keyword.volume)),
  };
}

function keywordCompetitionPasses(keyword: PreviewedKeyword): boolean {
  const competition = keyword.kp_competition?.toLowerCase();
  if (competition === 'low' || competition === 'medium') return true;
  if (keyword.kp_competition_index == null) return false;
  return keyword.kp_competition_index <= 50;
}

function keywordLowHangingFruitSignal(
  keyword: PreviewedKeyword,
  benchmarks: KeywordOpportunityBenchmarks,
): LowHangingFruitSignal {
  const hasBuyerIntent = BUYER_INTENT_RE.test(keyword.keyword);
  const volumeFloor = Math.max(100, benchmarks.medianVolume * 0.5);
  const hasEnoughVolume = keyword.volume >= volumeFloor;
  const cpcCeiling =
    benchmarks.medianCpc > 0 ? benchmarks.medianCpc * 1.25 : Number.POSITIVE_INFINITY;
  const hasReasonableCpc = keyword.cpc > 0 && keyword.cpc <= cpcCeiling;
  const hasAuctionData =
    keyword.source === 'keyword_planner' &&
    keywordHasCompetition(keyword) &&
    keywordHasBidRange(keyword);
  const competitionPasses = keywordCompetitionPasses(keyword);

  if (
    hasBuyerIntent &&
    hasEnoughVolume &&
    hasReasonableCpc &&
    competitionPasses &&
    keyword.in_account !== true
  ) {
    return {
      status: 'yes',
      label: 'Yes',
      reason: 'Buyer intent, usable volume, reasonable CPC, and low/medium competition.',
    };
  }

  if (hasBuyerIntent && hasEnoughVolume && keyword.in_account !== true) {
    const missing: string[] = [];
    if (!hasReasonableCpc) missing.push('CPC is high or missing');
    if (!competitionPasses) missing.push('competition is high or unknown');
    if (!hasAuctionData) missing.push('Planner bid/competition data is incomplete');
    return {
      status: 'review',
      label: 'Review',
      reason: missing.join('; ') || 'Commercial keyword, but it needs a manual review.',
    };
  }

  return {
    status: 'no',
    label: 'No',
    reason: !hasBuyerIntent
      ? 'No clear buyer-intent modifier.'
      : 'Volume is below the dynamic opportunity threshold or already in account.',
  };
}


function normalizedClusterText(value: string): string {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
}


function isNameMatchSingletonCluster(cluster: PreviewedCluster): boolean {
  if (cluster.keywords.length !== 1) return false;
  return (
    normalizedClusterText(cluster.cluster_name) ===
    normalizedClusterText(cluster.keywords[0]?.keyword ?? '')
  );
}


function CoverageMetricCard({
  label,
  value,
  detail,
  tone = 'neutral',
}: {
  label: string;
  value: string;
  detail: string;
  tone?: 'good' | 'neutral' | 'warning';
}) {
  const toneClass =
    tone === 'good'
      ? 'border-teal/30 bg-teal-light/20'
      : tone === 'warning'
        ? 'border-coral/40 bg-coral-light/20'
        : 'border-border bg-card';
  return (
    <div className={`rounded border px-3 py-2 ${toneClass}`}>
      <div className="text-[11px] font-medium uppercase text-muted-foreground">
        {label}
      </div>
      <div className="text-lg font-semibold tabular-nums">{value}</div>
      <div className="text-[11px] text-muted-foreground">{detail}</div>
    </div>
  );
}

function clusterOpportunityScore(
  cluster: PreviewedCluster,
  benchmarks: KeywordOpportunityBenchmarks,
): number {
  const lowFruitScore = cluster.keywords.reduce((score, keyword) => {
    const signal = keywordLowHangingFruitSignal(keyword, benchmarks);
    if (signal.status === 'yes') return score + 3;
    if (signal.status === 'review') return score + 1;
    return score;
  }, 0);
  const plannerCount = cluster.keywords.filter(
    (keyword) => keyword.source === 'keyword_planner',
  ).length;
  const bidCount = cluster.keywords.filter(keywordHasBidRange).length;
  const trendCount = cluster.keywords.filter(keywordHasMonthlyTrend).length;
  const totalVolume = cluster.keywords.reduce(
    (sum, keyword) => sum + keyword.volume,
    0,
  );
  const averageCpc =
    cluster.keywords.length > 0
      ? cluster.keywords.reduce((sum, keyword) => sum + keyword.cpc, 0) /
        cluster.keywords.length
      : 0;

  return (
    lowFruitScore * 1_000_000 +
    plannerCount * 50_000 +
    bidCount * 20_000 +
    trendCount * 10_000 +
    totalVolume -
    averageCpc * 10
  );
}

function compareClustersForDefaultView(
  a: PreviewedCluster,
  b: PreviewedCluster,
  benchmarks: KeywordOpportunityBenchmarks,
): number {
  const scoreDelta =
    clusterOpportunityScore(b, benchmarks) - clusterOpportunityScore(a, benchmarks);
  if (scoreDelta !== 0) return scoreDelta;
  const volumeDelta =
    b.keywords.reduce((sum, keyword) => sum + keyword.volume, 0) -
    a.keywords.reduce((sum, keyword) => sum + keyword.volume, 0);
  if (volumeDelta !== 0) return volumeDelta;
  return a.cluster_name.localeCompare(b.cluster_name);
}

function ResultsSectionNav({
  totalClusters,
  visibleClusters,
  negativesCount,
  qualityCount,
  showAllClusters,
  onToggleClusters,
}: {
  totalClusters: number;
  visibleClusters: number;
  negativesCount: number;
  qualityCount: number;
  showAllClusters: boolean;
  onToggleClusters: () => void;
}) {
  const hasMoreClusters = totalClusters > DEFAULT_VISIBLE_CLUSTER_COUNT;

  return (
    <div className="rounded border border-border bg-card p-3" id="keyword-research-sections">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <div>
          <div className="text-sm font-medium">Review sections</div>
          <div className="text-xs text-muted-foreground">
            Start with the recommendation set, then expand the full cluster list when needed.
          </div>
        </div>
        {hasMoreClusters && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-warm-bg"
            onClick={onToggleClusters}
          >
            {showAllClusters
              ? `Show top ${DEFAULT_VISIBLE_CLUSTER_COUNT}`
              : `Show all ${totalClusters}`}
          </Button>
        )}
      </div>
      <div className="grid gap-2 md:grid-cols-4">
        <SectionJump href="#keyword-research-overview" label="Overview" value="Metrics" />
        <SectionJump
          href="#keyword-research-negatives"
          label="Negative keywords"
          value={`${negativesCount} suggested`}
        />
        <SectionJump
          href="#keyword-research-quality"
          label="Quality gates"
          value={`${qualityCount} checks`}
        />
        <SectionJump
          href="#keyword-research-clusters"
          label="Ad group clusters"
          value={`${visibleClusters}/${totalClusters} shown`}
        />
      </div>
    </div>
  );
}

function SectionJump({
  href,
  label,
  value,
}: {
  href: string;
  label: string;
  value: string;
}) {
  return (
    <a
      href={href}
      className="rounded border border-border bg-warm-bg px-3 py-2 text-sm hover:border-teal/40 hover:bg-teal-light/20"
    >
      <div className="font-medium text-foreground">{label}</div>
      <div className="text-xs text-muted-foreground">{value}</div>
    </a>
  );
}


function ClustersResult({
  clusters,
  negatives,
  qualityChecks,
  targetKeywordSuggestions,
  plannerLanguage,
  plannerGeoTargetConstants,
  plannerSiteFilterUrl,
  selectedLocations,
  researchRunId,
  clustersOnly = false,
}: {
  clusters: PreviewedCluster[];
  negatives: NegativeKeywordCandidate[];
  qualityChecks: KeywordResearchQualityCheck[];
  targetKeywordSuggestions: TargetKeywordSuggestion[];
  plannerLanguage: string | null;
  plannerGeoTargetConstants: string[];
  plannerSiteFilterUrl: string | null;
  selectedLocations: GeoTargetSuggestion[];
  researchRunId: string | null;
  clustersOnly?: boolean;
}) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const tenantId = useTenantStore((s) => s.currentTenant ?? '');
  const benchmarks = useMemo(
    () => buildKeywordOpportunityBenchmarks(clusters),
    [clusters],
  );
  const clusterViewSignature = useMemo(
    () => clusters.map((cluster) => cluster.cluster_name).join('|'),
    [clusters],
  );
  const [showAllClusters, setShowAllClusters] = useState(false);
  // Accordion-style collapse per cluster (keyed by cluster_name).
  // A name absent from the set is expanded — default is expanded.
  const [collapsedClusterNames, setCollapsedClusterNames] = useState<Set<string>>(
    () => new Set(),
  );
  useEffect(() => {
    setShowAllClusters(false);
    setCollapsedClusterNames(new Set());
  }, [clusterViewSignature]);
  const toggleClusterCollapsed = (clusterName: string) => {
    setCollapsedClusterNames((current) => {
      const next = new Set(current);
      if (next.has(clusterName)) {
        next.delete(clusterName);
      } else {
        next.add(clusterName);
      }
      return next;
    });
  };
  const rankedClusters = useMemo(
    () =>
      [...clusters].sort((a, b) =>
        compareClustersForDefaultView(a, b, benchmarks),
      ),
    [benchmarks, clusters],
  );
  const visibleClusters = showAllClusters
    ? rankedClusters
    : rankedClusters.slice(0, DEFAULT_VISIBLE_CLUSTER_COUNT);
  const allVisibleClustersCollapsed =
    visibleClusters.length > 0 &&
    visibleClusters.every((cluster) =>
      collapsedClusterNames.has(cluster.cluster_name),
    );
  const defaultSelectedKeys = useMemo(() => {
    const keys = new Set<string>();
    clusters.forEach((cluster) => {
      cluster.keywords.forEach((keyword) => {
        if (
          keyword.selected_for_campaign ||
          keywordLowHangingFruitSignal(keyword, benchmarks).status === 'yes'
        ) {
          keys.add(keywordSelectionKey(keyword.keyword));
        }
      });
    });
    return keys;
  }, [benchmarks, clusters]);
  const [selectedKeywordKeys, setSelectedKeywordKeys] = useState<Set<string>>(
    () => new Set(defaultSelectedKeys),
  );
  const defaultSelectedSignature = useMemo(
    () => Array.from(defaultSelectedKeys).sort().join('|'),
    [defaultSelectedKeys],
  );
  useEffect(() => {
    setSelectedKeywordKeys(new Set(defaultSelectedKeys));
  }, [defaultSelectedKeys, defaultSelectedSignature]);
  const totalKeywords = clusters.reduce((n, c) => n + c.keywords.length, 0);
  const totalVolume = clusters.reduce(
    (n, c) => n + c.keywords.reduce((m, kw) => m + kw.volume, 0),
    0,
  );
  const totalCpc = clusters.reduce(
    (n, c) => n + c.keywords.reduce((m, kw) => m + kw.cpc, 0),
    0,
  );
  const avgCpc = totalKeywords > 0 ? totalCpc / totalKeywords : 0;
  const keywordPlannerCount = clusters.reduce(
    (n, c) =>
      n + c.keywords.filter((kw) => kw.source === 'keyword_planner').length,
    0,
  );
  const competitionCount = clusters.reduce(
    (n, c) => n + c.keywords.filter(keywordHasCompetition).length,
    0,
  );
  const bidRangeCount = clusters.reduce(
    (n, c) => n + c.keywords.filter(keywordHasBidRange).length,
    0,
  );
  const trendKeywordCount = clusters.reduce(
    (n, c) => n + c.keywords.filter(keywordHasMonthlyTrend).length,
    0,
  );
  const singletonClusters = clusters.filter((c) => c.keywords.length === 1).length;
  const nameMatchSingletons = clusters.filter(isNameMatchSingletonCluster).length;
  const plannerLanguageLabel = languageLabel(plannerLanguage);
  const plannerGeoLabel = geoTargetLabel(
    plannerGeoTargetConstants,
    selectedLocations,
  );
  // Phase F2: fetch the tenant's published pages once so each cluster
  // card can flag content gaps (cluster has a landing_page URL but the
  // tenant doesn't have a published page at that URL).
  const tenantPagesQuery = useQuery({
    queryKey: ['tenant-pages', tenantId],
    queryFn: () => fetchTenantPages(tenantId),
    enabled: !!tenantId,
    staleTime: 5 * 60 * 1000,
  });

  const publishedPages = useMemo(
    () =>
      new Set(
        (tenantPagesQuery.data?.pages ?? []).map((p) => normalizeUrl(p)),
      ),
    [tenantPagesQuery.data],
  );

  const gapCount = clusters.filter((c) =>
    isContentGap(c, publishedPages),
  ).length;
  const qualitySummary = summarizeQualityChecks(qualityChecks);
  const keywordRows = useMemo(() => {
    const rows: Array<{ keyword: PreviewedKeyword; cluster: PreviewedCluster }> = [];
    const seen = new Set<string>();
    clusters.forEach((cluster) => {
      cluster.keywords.forEach((keyword) => {
        const key = keywordSelectionKey(keyword.keyword);
        if (seen.has(key)) return;
        seen.add(key);
        rows.push({ keyword, cluster });
      });
    });
    return rows;
  }, [clusters]);
  const selectedRows = useMemo(() => {
    return keywordRows.filter((row) =>
      selectedKeywordKeys.has(keywordSelectionKey(row.keyword.keyword)),
    );
  }, [keywordRows, selectedKeywordKeys]);
  const lowFruitKeywordRows = useMemo(() => {
    return keywordRows.filter(
      (row) => keywordLowHangingFruitSignal(row.keyword, benchmarks).status === 'yes',
    );
  }, [benchmarks, keywordRows]);
  const allKeywordsSelected =
    keywordRows.length > 0 &&
    keywordRows.every((row) =>
      selectedKeywordKeys.has(keywordSelectionKey(row.keyword.keyword)),
    );
  const lowFruitCount = lowFruitKeywordRows.length;
  const allLowFruitSelected =
    lowFruitKeywordRows.length > 0 &&
    lowFruitKeywordRows.every((row) =>
      selectedKeywordKeys.has(keywordSelectionKey(row.keyword.keyword)),
    );

  const clearHistoryRunCache = () => {
    queryClient.removeQueries({
      queryKey: keywordResearchHistoryQueryKeys.lists(),
    });
    void queryClient.invalidateQueries({
      queryKey: keywordResearchHistoryQueryKeys.lists(),
    });
    queryClient.removeQueries({
      queryKey: keywordResearchHistoryQueryKeys.active(tenantId),
    });
    void queryClient.invalidateQueries({
      queryKey: keywordResearchHistoryQueryKeys.active(tenantId),
    });
    if (researchRunId) {
      queryClient.removeQueries({
        queryKey: keywordResearchHistoryQueryKeys.detail(researchRunId),
      });
      void queryClient.invalidateQueries({
        queryKey: keywordResearchHistoryQueryKeys.detail(researchRunId),
      });
    }
  };

  const openSavedRun = () => {
    if (!researchRunId) return;
    clearHistoryRunCache();
    navigate(
      `/ads-tools/keyword-research?platform=google-ads&research_tab=history&run=${researchRunId}`,
    );
  };

  const selectAllKeywords = () => {
    setSelectedKeywordKeys(
      new Set(keywordRows.map((row) => keywordSelectionKey(row.keyword.keyword))),
    );
  };
  const selectLowFruitKeywords = () => {
    setSelectedKeywordKeys(
      new Set(lowFruitKeywordRows.map((row) => keywordSelectionKey(row.keyword.keyword))),
    );
  };
  const clearKeywordSelection = () => {
    setSelectedKeywordKeys(new Set());
  };
  const selectedKeywords = selectedRows.map((row) => row.keyword.keyword);
  const selectedPages = Array.from(
    new Set(
      selectedRows
        .map((row) => row.cluster.landing_page)
        .filter((value): value is string => Boolean(value)),
    ),
  );
  const selectedGapClusters = Array.from(
    new Set(
      selectedRows
        .filter((row) => isContentGap(row.cluster, publishedPages))
        .map((row) => row.cluster.cluster_name),
    ),
  );
  const selectedGapCount = selectedGapClusters.length;
  const saveSelectionMutation = useMutation({
    mutationFn: (keywords: string[]) => {
      if (!researchRunId) {
        return Promise.reject(
          new Error('This research run was not persisted. Run keyword research again before saving a selection.'),
        );
      }
      return saveKeywordResearchSelection(researchRunId, {
        tenant_id: tenantId,
        selected_keywords: keywords,
      });
    },
  });

  const handleKeywordToggle = (keyword: string, checked: boolean) => {
    setSelectedKeywordKeys((prev) => {
      const next = new Set(prev);
      const key = keywordSelectionKey(keyword);
      if (checked) next.add(key);
      else next.delete(key);
      return next;
    });
  };

  const persistSelectedHandoff = async ({ openCampaigns }: { openCampaigns: boolean }) => {
    if (selectedKeywords.length === 0) {
      toast.warning('Select at least one keyword before saving for campaign setup.');
      return;
    }
    if (openCampaigns && selectedGapCount > 0) {
      toast.warning(
        `Resolve landing-page gaps before campaign setup: ${selectedGapClusters.slice(0, 3).join(', ')}`,
      );
      return;
    }
    let selectionSavedAt: string | null | undefined;
    try {
      const selectionResult = await saveSelectionMutation.mutateAsync(selectedKeywords);
      selectionSavedAt = selectionResult.selection_saved_at;
      clearHistoryRunCache();
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail ?? String(err);
      toast.error(`Could not save selected keywords: ${detail}`);
      return;
    }
    const handoff: KeywordResearchCampaignHandoff = {
      source: 'keyword_research',
      tenant_id: tenantId,
      run_id: researchRunId,
      selected_keywords: selectedKeywords,
      selected_keyword_details: selectedRows.map((row) => {
        const signal = keywordLowHangingFruitSignal(row.keyword, benchmarks);
        return {
          ...row.keyword,
          cluster_name: row.cluster.cluster_name,
          landing_page: row.cluster.landing_page,
          low_hanging_fruit: signal,
        };
      }),
      pages: selectedPages,
      saved_at: new Date().toISOString(),
      planner_language_label: plannerLanguageLabel,
      planner_geo_label: plannerGeoLabel,
      site_filter_url: plannerSiteFilterUrl,
    };
    window.localStorage.setItem(
      keywordResearchCampaignHandoffKey(tenantId),
      JSON.stringify(handoff),
    );
      toast.success(
        `${selectedKeywords.length} selected keyword${selectedKeywords.length !== 1 ? 's' : ''} ${
          openCampaigns
            ? 'sent to Campaigns as a setup package'
            : 'saved to this history run'
        }` +
          (selectionSavedAt ? ` · ${new Date(selectionSavedAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}` : ''),
      );
    if (openCampaigns) {
      navigate('/ads-tools/campaigns?platform=google-ads');
    } else if (researchRunId) {
      openSavedRun();
    }
  };

  const handleExportCsv = () => {
    downloadClustersCsv(clusters, tenantId);
    toast.success('Keyword cluster CSV exported');
  };

  const handleCopyCsv = async () => {
    try {
      await copyClustersCsv(clusters);
      toast.success('Keyword cluster CSV copied');
    } catch {
      toast.error('Could not copy CSV in this browser');
    }
  };

  const handleEmailApproval = () => {
    const tenant = workspaceDomain(tenantId) || tenantId || 'workspace';
    const subject = encodeURIComponent(
      `${tenant} Google Ads keyword clusters for approval`,
    );
    const body = encodeURIComponent(
      buildApprovalEmailBody(
        clusters,
        negatives,
        tenantId,
        qualityChecks,
        targetKeywordSuggestions,
        plannerLanguageLabel,
        plannerGeoLabel,
        plannerSiteFilterUrl,
      ),
    );
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopyApprovalEmail = async () => {
    try {
      await copyApprovalEmail(
        clusters,
        negatives,
        tenantId,
        qualityChecks,
        targetKeywordSuggestions,
        plannerLanguageLabel,
        plannerGeoLabel,
        plannerSiteFilterUrl,
      );
      toast.success('Approval email copied');
    } catch {
      toast.error('Could not copy approval email in this browser');
    }
  };

  return (
    <section className="space-y-4">
      <div>
        <CardTitle className="flex items-center gap-2">
          <Search className="h-4 w-4 text-teal" />
          {clusters.length} ad-group cluster{clusters.length !== 1 ? 's' : ''}
          {gapCount > 0 && (
            <Badge
              variant="outline"
              className="border-coral/40 bg-coral-light text-coral-dark text-[10px]"
            >
              <TriangleAlert className="mr-1 h-2.5 w-2.5" />
              {gapCount} content gap{gapCount !== 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {totalKeywords} keywords · combined search volume{' '}
          {totalVolume.toLocaleString()}/mo. Each cluster becomes one ad
          group when you commit to setup on{' '}
          <code>/ads-tools/campaigns</code>.
          {gapCount > 0 && (
            <>
              {' '}Content gaps means demand exists but no published service
              page yet — opportunity for organic capture too.
            </>
          )}
        </CardDescription>
      </div>
      <Tabs defaultValue={clustersOnly ? 'clusters' : 'overview'} className="space-y-4">
        <div className={clustersOnly ? 'min-w-0' : 'grid gap-4 lg:grid-cols-[220px_minmax(0,1fr)]'}>
          {!clustersOnly && (
            <div className="space-y-3 lg:sticky lg:top-4 lg:self-start">
              <TabsList className="grid h-auto w-full grid-cols-2 gap-1 bg-warm-bg p-1 lg:grid-cols-1">
                <TabsTrigger value="overview" className="justify-start">
                  Overview
                </TabsTrigger>
                <TabsTrigger value="negatives" className="justify-start">
                  Negatives
                </TabsTrigger>
                <TabsTrigger value="quality" className="justify-start">
                  Quality
                </TabsTrigger>
                <TabsTrigger value="clusters" className="justify-start">
                  Clusters
                </TabsTrigger>
              </TabsList>
              <div className="rounded border border-border bg-card p-3 text-xs">
                <div className="font-medium text-foreground">Section shortcuts</div>
                <div className="mt-2 space-y-1 text-muted-foreground">
                  <div>{clusters.length} clusters</div>
                  <div>{totalKeywords} keywords</div>
                  <div>{negatives.length} negative candidates</div>
                  <div>{qualityChecks.length} quality checks</div>
                </div>
                {clusters.length > DEFAULT_VISIBLE_CLUSTER_COUNT && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="mt-3 h-8 w-full bg-warm-bg text-xs"
                    onClick={() => setShowAllClusters((value) => !value)}
                  >
                    {showAllClusters
                      ? `Show top ${DEFAULT_VISIBLE_CLUSTER_COUNT}`
                      : `Show all ${clusters.length}`}
                  </Button>
                )}
              </div>
            </div>
          )}

          <div className="min-w-0 space-y-4">
            {!clustersOnly && (
              <>
            <TabsContent value="overview" className="mt-0">
              <Accordion
                type="multiple"
                defaultValue={['overview-metrics']}
                className="space-y-3"
              >
                <AccordionItem
                  value="overview-metrics"
                  className="rounded border border-border bg-card px-3"
                >
                  <AccordionTrigger className="py-3 text-sm font-medium hover:no-underline">
                    Overview metrics
                  </AccordionTrigger>
                  <AccordionContent className="space-y-4 pb-3">

        <div
          id="keyword-research-overview"
          className="grid scroll-mt-24 gap-2 sm:grid-cols-2 lg:grid-cols-4"
        >
          <div className="rounded border border-border bg-warm-bg px-3 py-2">
            <div className="text-[11px] font-medium uppercase text-muted-foreground">
              Clusters
            </div>
            <div className="text-lg font-semibold tabular-nums">
              {clusters.length}
            </div>
          </div>
          <div className="rounded border border-border bg-warm-bg px-3 py-2">
            <div className="text-[11px] font-medium uppercase text-muted-foreground">
              Keywords
            </div>
            <div className="text-lg font-semibold tabular-nums">
              {totalKeywords}
            </div>
          </div>
          <div className="rounded border border-border bg-warm-bg px-3 py-2">
            <div className="text-[11px] font-medium uppercase text-muted-foreground">
              Avg CPC
            </div>
            <div className="text-lg font-semibold tabular-nums">
              ${avgCpc.toFixed(2)}
            </div>
          </div>
          <div className="rounded border border-border bg-warm-bg px-3 py-2">
            <div className="text-[11px] font-medium uppercase text-muted-foreground">
              Planner ideas
            </div>
            <div className="text-lg font-semibold tabular-nums">
              {keywordPlannerCount}
            </div>
          </div>
        </div>

        <div className="grid gap-2 md:grid-cols-4">
          <CoverageMetricCard
            label="Planner coverage"
            value={`${keywordPlannerCount}/${totalKeywords}`}
            detail="Google Ads rows"
            tone={keywordPlannerCount > 0 ? 'good' : 'warning'}
          />
          <CoverageMetricCard
            label="Bid ranges"
            value={`${bidRangeCount}/${totalKeywords}`}
            detail="Keyword Planner bid data"
            tone={bidRangeCount > 0 ? 'good' : 'warning'}
          />
          <CoverageMetricCard
            label="Monthly trends"
            value={`${trendKeywordCount}/${totalKeywords}`}
            detail="Keyword Planner history"
            tone={trendKeywordCount > 0 ? 'good' : 'warning'}
          />
          <CoverageMetricCard
            label="Cluster health"
            value={`${singletonClusters}/${clusters.length}`}
            detail={
              nameMatchSingletons > 0
                ? `${nameMatchSingletons} name-match`
                : 'singleton clusters'
            }
            tone={nameMatchSingletons > 0 ? 'warning' : 'neutral'}
          />
        </div>

        <div className="rounded border border-border bg-card px-3 py-2 text-sm">
          <div className="text-[11px] font-medium uppercase text-muted-foreground">
            Keyword Planner targeting
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1">
            <span>
              <span className="text-muted-foreground">Language:</span>{' '}
              {plannerLanguageLabel}
            </span>
            <span>
              <span className="text-muted-foreground">Market:</span>{' '}
              {plannerGeoLabel}
            </span>
            <span>
              <span className="text-muted-foreground">Site filter:</span>{' '}
              {plannerSiteFilterUrl ? (
                <a
                  href={plannerSiteFilterUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-teal-dark hover:underline"
                >
                  {plannerSiteFilterUrl}
                </a>
              ) : (
                'None'
              )}
            </span>
          </div>
        </div>

        {keywordPlannerCount === 0 && (
          <div className="rounded border border-coral/40 bg-coral-light/20 p-3 text-sm">
            <div className="flex items-start gap-2">
              <TriangleAlert className="mt-0.5 h-4 w-4 flex-shrink-0 text-coral-dark" />
              <div>
                <div className="font-medium text-coral-dark">
                  No Google Ads Keyword Planner rows are attached
                </div>
              <p className="text-xs text-muted-foreground">
                  These results are seed fallback rows. Check the Google
                  Ads customer ID, OAuth connection, location target, and
                  GOOGLE_ADS_KEYWORD_PLANNER_ENABLED before using this for an
                  approval handoff.
                </p>
              </div>
            </div>
          </div>
        )}
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            <TabsContent value="negatives" className="mt-0">
              <Accordion
                type="multiple"
                defaultValue={['negative-keywords']}
                className="space-y-3"
              >
                <AccordionItem
                  value="negative-keywords"
                  className="rounded border border-border bg-card px-3"
                >
                  <AccordionTrigger className="py-3 text-sm font-medium hover:no-underline">
                    Suggested negative keywords
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <div id="keyword-research-negatives" className="scroll-mt-24">
                      {negatives.length > 0 ? (
                        <SuggestedNegativesPanel negatives={negatives} />
                      ) : (
                        <div className="rounded border border-border bg-card p-4 text-sm">
                          <div className="font-medium">No suggested negative keywords</div>
                          <p className="mt-1 text-xs text-muted-foreground">
                            The keyword filter did not return reviewable exclusions for this run.
                          </p>
                        </div>
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>
              </Accordion>
            </TabsContent>

            <TabsContent value="quality" className="mt-0">
              <Accordion
                type="multiple"
                defaultValue={['quality-gates', 'seed-keywords']}
                className="space-y-3"
              >
                <AccordionItem
                  value="quality-gates"
                  className="rounded border border-border bg-card px-3"
                >
                  <AccordionTrigger className="py-3 text-sm font-medium hover:no-underline">
                    Quality gates
                  </AccordionTrigger>
                  <AccordionContent className="pb-3">
                    <div id="keyword-research-quality" className="scroll-mt-24">
                      {qualityChecks.length > 0 && (
                        <QualityChecksPanel checks={qualityChecks} summary={qualitySummary} />
                      )}
                    </div>
                  </AccordionContent>
                </AccordionItem>

                {targetKeywordSuggestions.length > 0 && (
                  <AccordionItem
                    value="seed-keywords"
                    className="rounded border border-border bg-card px-3"
                  >
                    <AccordionTrigger className="py-3 text-sm font-medium hover:no-underline">
                      Seed keywords used
                    </AccordionTrigger>
                    <AccordionContent className="pb-3">
                      <TargetKeywordSeedsPanel suggestions={targetKeywordSuggestions} />
                    </AccordionContent>
                  </AccordionItem>
                )}
              </Accordion>
            </TabsContent>
              </>
            )}

            <TabsContent value="clusters" className="mt-0 space-y-4">
        <div className="sticky top-2 z-10 rounded border border-teal/30 bg-teal-light/20 p-3 shadow-sm">
          <div className="flex flex-wrap items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-teal-dark" />
            <div className="mr-auto">
              <div className="text-sm font-medium text-foreground">
                Selected keyword actions
              </div>
              <div className="text-xs text-muted-foreground">
                {selectedKeywords.length}/{keywordRows.length} selected. Research is saved when results load; this updates the keyword selection on that existing history run.
                {!researchRunId && ' This run was not persisted, so selection saving is unavailable.'}
                {selectedGapCount > 0 && ` ${selectedGapCount} selected cluster${selectedGapCount !== 1 ? 's have' : ' has'} landing-page gaps.`}
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-card"
              disabled={lowFruitCount === 0 || allLowFruitSelected}
              onClick={selectLowFruitKeywords}
            >
              Select low fruit ({lowFruitCount})
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-card"
              disabled={keywordRows.length === 0 || allKeywordsSelected}
              onClick={selectAllKeywords}
            >
              Select all results
            </Button>
            <Button
              type="button"
              variant="outline"
              size="sm"
              className="bg-card"
              disabled={selectedKeywords.length === 0}
              onClick={clearKeywordSelection}
            >
              Clear
            </Button>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            disabled={selectedKeywords.length === 0 || !researchRunId || saveSelectionMutation.isPending}
            onClick={() => persistSelectedHandoff({ openCampaigns: false })}
          >
            {saveSelectionMutation.isPending ? (
              <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" />
            ) : (
              <CheckCircle2 className="mr-1 h-3.5 w-3.5" />
            )}
             Save selection and view run
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            disabled={!researchRunId}
            onClick={openSavedRun}
          >
            <History className="mr-1 h-3.5 w-3.5" />
            View saved run
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            onClick={handleExportCsv}
          >
            <Download className="mr-1 h-3.5 w-3.5" />
            Export CSV
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            onClick={handleCopyCsv}
          >
            <Copy className="mr-1 h-3.5 w-3.5" />
            Copy CSV
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            onClick={handleCopyApprovalEmail}
          >
            <Copy className="mr-1 h-3.5 w-3.5" />
            Copy email
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="bg-card"
            onClick={handleEmailApproval}
          >
            <Mail className="mr-1 h-3.5 w-3.5" />
            Email approval
          </Button>
          <Button
            type="button"
            size="sm"
            className="bg-coral text-white hover:bg-coral-dark"
            disabled={selectedKeywords.length === 0 || !researchRunId || selectedGapCount > 0 || saveSelectionMutation.isPending}
            onClick={() => persistSelectedHandoff({ openCampaigns: true })}
          >
            Review in Campaigns
            <ArrowRight className="ml-1 h-3.5 w-3.5" />
          </Button>
          </div>
        </div>

        <div id="keyword-research-clusters" className="scroll-mt-24 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2 rounded border border-border bg-card px-3 py-2">
            <div>
              <div className="text-sm font-medium">
                {showAllClusters
                  ? `All ${clusters.length} ad group clusters`
                  : `Top ${Math.min(DEFAULT_VISIBLE_CLUSTER_COUNT, clusters.length)} ad group clusters`}
              </div>
              <div className="text-xs text-muted-foreground">
                Default view is ranked by low-hanging-fruit signals, Planner coverage,
                bid data, trend data, and volume.
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              {visibleClusters.length > 1 && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-warm-bg"
                  onClick={() =>
                    setCollapsedClusterNames(
                      allVisibleClustersCollapsed
                        ? new Set()
                        : new Set(
                            rankedClusters.map((cluster) => cluster.cluster_name),
                          ),
                    )
                  }
                >
                  {allVisibleClustersCollapsed ? 'Expand all' : 'Collapse all'}
                </Button>
              )}
              {clusters.length > DEFAULT_VISIBLE_CLUSTER_COUNT && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="bg-warm-bg"
                  onClick={() => setShowAllClusters((value) => !value)}
                >
                  {showAllClusters
                    ? `Show top ${DEFAULT_VISIBLE_CLUSTER_COUNT}`
                    : `Show all ${clusters.length}`}
                </Button>
              )}
            </div>
          </div>
          {visibleClusters.map((cluster, idx) => (
            <ClusterCard
              key={`${cluster.cluster_name}-${idx}`}
              cluster={cluster}
              isGap={isContentGap(cluster, publishedPages)}
              benchmarks={benchmarks}
              selectedKeywordKeys={selectedKeywordKeys}
              onKeywordToggle={handleKeywordToggle}
              expanded={!collapsedClusterNames.has(cluster.cluster_name)}
              onToggleExpanded={() => toggleClusterCollapsed(cluster.cluster_name)}
            />
          ))}
        </div>
            </TabsContent>
          </div>
        </div>
      </Tabs>
    </section>
  );
}

/**
 * Content-gap rule (Phase F2):
 *   - cluster.landing_page is null/empty → gap (no page assigned at all)
 *   - cluster.landing_page is set but normalized URL is NOT in the
 *     tenant's published-pages set → gap (URL is fictional or unpublished)
 *   - otherwise → not a gap
 *
 * If publishedPages is empty (still loading or tenant has no pages),
 * we conservatively treat null landing_page as the only gap signal so
 * we don't false-positive every cluster.
 */
function isContentGap(
  cluster: PreviewedCluster,
  publishedPages: Set<string>,
): boolean {
  const lp = cluster.landing_page;
  if (!lp) return true;
  if (publishedPages.size === 0) return false;
  return !publishedPages.has(normalizeUrl(lp));
}


function LowHangingFruitBadge({
  signal,
}: {
  signal: LowHangingFruitSignal;
}) {
  const className =
    signal.status === 'yes'
      ? 'border-teal/30 bg-teal-light/20 text-teal-dark'
      : signal.status === 'review'
        ? 'border-coral/40 bg-coral-light/20 text-coral-dark'
        : 'bg-card text-muted-foreground';
  return (
    <Badge variant="outline" className={className} title={signal.reason}>
      {signal.status === 'yes' && <CheckCircle2 className="mr-1 h-2.5 w-2.5" />}
      {signal.label}
    </Badge>
  );
}


function HeaderTooltip({
  label,
  description,
}: {
  label: string;
  description: string;
}) {
  return (
    <TooltipProvider delayDuration={150}>
      <UiTooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex cursor-help items-center justify-end border-b border-dotted border-muted-foreground/50">
            {label}
          </span>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs text-xs leading-relaxed">
          {description}
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
}


type KeywordTableColumnKey =
  | 'use'
  | 'keyword'
  | 'volume'
  | 'cpc'
  | 'trend'
  | 'lowFruit'
  | 'competition'
  | 'bid'
  | 'account'
  | 'source';

type KeywordTableSortKey = Exclude<KeywordTableColumnKey, 'use'>;
type KeywordTableSort = {
  key: KeywordTableSortKey;
  direction: 'asc' | 'desc';
};
type KeywordTableFilter =
  | 'all'
  | 'selected'
  | 'low_fruit'
  | 'new'
  | 'planner'
  | 'has_trend'
  | 'missing_bid';

const KEYWORD_TABLE_COLUMNS: KeywordTableColumnKey[] = [
  'use',
  'keyword',
  'volume',
  'cpc',
  'trend',
  'lowFruit',
  'competition',
  'bid',
  'account',
  'source',
];

const DEFAULT_KEYWORD_COLUMN_WIDTHS: Record<KeywordTableColumnKey, number> = {
  use: 52,
  keyword: 260,
  volume: 96,
  cpc: 82,
  trend: 118,
  lowFruit: 112,
  competition: 116,
  bid: 150,
  account: 104,
  source: 112,
};

const MIN_KEYWORD_COLUMN_WIDTHS: Record<KeywordTableColumnKey, number> = {
  use: 44,
  keyword: 180,
  volume: 78,
  cpc: 70,
  trend: 92,
  lowFruit: 92,
  competition: 96,
  bid: 118,
  account: 88,
  source: 92,
};

function keywordTrendLatestSearches(keyword: PreviewedKeyword): number {
  const volumes = keyword.kp_monthly_search_volumes ?? [];
  const latest = volumes[volumes.length - 1]?.monthly_searches;
  return latest ?? keyword.kp_avg_monthly_searches ?? keyword.volume ?? 0;
}

function keywordBidMicros(keyword: PreviewedKeyword): number {
  return (
    keyword.kp_high_top_of_page_bid_micros ??
    keyword.kp_low_top_of_page_bid_micros ??
    0
  );
}

function keywordLowFruitRank(signal: LowHangingFruitSignal): number {
  if (signal.status === 'yes') return 2;
  if (signal.status === 'review') return 1;
  return 0;
}

function keywordCompetitionRank(keyword: PreviewedKeyword): number {
  const competition = keyword.kp_competition?.toLowerCase();
  if (competition === 'low') return 3;
  if (competition === 'medium') return 2;
  if (competition === 'high') return 1;
  return 0;
}

function keywordAccountRank(keyword: PreviewedKeyword): number {
  if (keyword.in_account === false) return 2;
  if (keyword.in_account == null) return 1;
  return 0;
}

function keywordSortValue(
  keyword: PreviewedKeyword,
  sortKey: KeywordTableSortKey,
  benchmarks: KeywordOpportunityBenchmarks,
): string | number {
  switch (sortKey) {
    case 'keyword':
      return keyword.keyword.toLowerCase();
    case 'volume':
      return keyword.volume;
    case 'cpc':
      return keyword.cpc;
    case 'trend':
      return keywordTrendLatestSearches(keyword);
    case 'lowFruit':
      return keywordLowFruitRank(keywordLowHangingFruitSignal(keyword, benchmarks));
    case 'competition':
      return keywordCompetitionRank(keyword);
    case 'bid':
      return keywordBidMicros(keyword);
    case 'account':
      return keywordAccountRank(keyword);
    case 'source':
      return keyword.source ?? '';
  }
}

function keywordMatchesTableFilter(
  keyword: PreviewedKeyword,
  filter: KeywordTableFilter,
  selectedKeywordKeys: Set<string>,
  benchmarks: KeywordOpportunityBenchmarks,
): boolean {
  switch (filter) {
    case 'selected':
      return selectedKeywordKeys.has(keywordSelectionKey(keyword.keyword));
    case 'low_fruit':
      return keywordLowHangingFruitSignal(keyword, benchmarks).status === 'yes';
    case 'new':
      return keyword.in_account === false;
    case 'planner':
      return keyword.source === 'keyword_planner';
    case 'has_trend':
      return keywordHasMonthlyTrend(keyword);
    case 'missing_bid':
      return !keywordHasBidRange(keyword);
    case 'all':
    default:
      return true;
  }
}

function compareKeywordsForTable(
  a: PreviewedKeyword,
  b: PreviewedKeyword,
  sort: KeywordTableSort,
  benchmarks: KeywordOpportunityBenchmarks,
): number {
  const aValue = keywordSortValue(a, sort.key, benchmarks);
  const bValue = keywordSortValue(b, sort.key, benchmarks);
  const comparison =
    typeof aValue === 'number' && typeof bValue === 'number'
      ? aValue - bValue
      : String(aValue).localeCompare(String(bValue));
  return sort.direction === 'asc' ? comparison : -comparison;
}


function ClusterCard({
  cluster,
  isGap,
  benchmarks,
  selectedKeywordKeys,
  onKeywordToggle,
  expanded,
  onToggleExpanded,
}: {
  cluster: PreviewedCluster;
  isGap: boolean;
  benchmarks: KeywordOpportunityBenchmarks;
  selectedKeywordKeys: Set<string>;
  onKeywordToggle: (keyword: string, checked: boolean) => void;
  /** Accordion state — header stays visible, body collapses. */
  expanded: boolean;
  onToggleExpanded: () => void;
}) {
  const navigate = useNavigate();
  const totalVolume = cluster.keywords.reduce((n, kw) => n + kw.volume, 0);
  const avgCpc =
    cluster.keywords.length > 0
      ? cluster.keywords.reduce((n, kw) => n + kw.cpc, 0) / cluster.keywords.length
      : 0;
  const plannerCount = cluster.keywords.filter(
    (kw) => kw.source === 'keyword_planner',
  ).length;
  const newKeywordCount = cluster.keywords.filter(
    (kw) => kw.in_account === false,
  ).length;
  const existingKeywordCount = cluster.keywords.filter(
    (kw) => kw.in_account === true,
  ).length;
  const unknownKeywordCount =
    cluster.keywords.length - newKeywordCount - existingKeywordCount;
  const competitionCount = cluster.keywords.filter(keywordHasCompetition).length;
  const bidRangeCount = cluster.keywords.filter(keywordHasBidRange).length;
  const trendCount = cluster.keywords.filter(keywordHasMonthlyTrend).length;
  const isNameMatchSingleton = isNameMatchSingletonCluster(cluster);
  const clusterTrendData = buildKeywordPlannerTrendData([cluster]);
  const [keywordSearch, setKeywordSearch] = useState('');
  const [keywordFilter, setKeywordFilter] = useState<KeywordTableFilter>('all');
  const [keywordSort, setKeywordSort] = useState<KeywordTableSort>({
    key: 'volume',
    direction: 'desc',
  });
  const [columnWidths, setColumnWidths] = useState(DEFAULT_KEYWORD_COLUMN_WIDTHS);

  const visibleKeywords = useMemo(() => {
    // Fuzzy (lib/fuzzyMatch). The table's own column sort decides the order.
    const byFilter = cluster.keywords.filter((keyword) =>
      keywordMatchesTableFilter(
        keyword,
        keywordFilter,
        selectedKeywordKeys,
        benchmarks,
      ),
    );
    return fuzzyFilter(
      byFilter,
      keywordSearch,
      (keyword) => [keyword.keyword, keyword.source, keyword.kp_competition],
      { sort: false },
    ).sort((a, b) => compareKeywordsForTable(a, b, keywordSort, benchmarks));
  }, [
    benchmarks,
    cluster.keywords,
    keywordFilter,
    keywordSearch,
    keywordSort,
    selectedKeywordKeys,
  ]);
  const visibleSelectedCount = visibleKeywords.filter((keyword) =>
    selectedKeywordKeys.has(keywordSelectionKey(keyword.keyword)),
  ).length;
  const allVisibleSelected =
    visibleKeywords.length > 0 && visibleSelectedCount === visibleKeywords.length;
  const someVisibleSelected = visibleSelectedCount > 0 && !allVisibleSelected;

  const toggleSort = (key: KeywordTableSortKey) => {
    setKeywordSort((current) =>
      current.key === key
        ? {
            key,
            direction: current.direction === 'asc' ? 'desc' : 'asc',
          }
        : {
            key,
            direction: key === 'keyword' || key === 'source' ? 'asc' : 'desc',
          },
    );
  };

  const resizeColumn = (
    columnKey: KeywordTableColumnKey,
    event: ReactPointerEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const startX = event.clientX;
    const startWidth = columnWidths[columnKey] ?? DEFAULT_KEYWORD_COLUMN_WIDTHS[columnKey];
    const minWidth = MIN_KEYWORD_COLUMN_WIDTHS[columnKey];
    const previousCursor = document.body.style.cursor;
    document.body.style.cursor = 'col-resize';
    const onPointerMove = (moveEvent: PointerEvent) => {
      const nextWidth = Math.max(minWidth, startWidth + moveEvent.clientX - startX);
      setColumnWidths((current) => ({
        ...current,
        [columnKey]: nextWidth,
      }));
    };
    const onPointerUp = () => {
      document.body.style.cursor = previousCursor;
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  const toggleAllVisibleKeywords = (checked: boolean) => {
    for (const keyword of visibleKeywords) {
      onKeywordToggle(keyword.keyword, checked);
    }
  };

  const renderHeader = (
    columnKey: KeywordTableColumnKey,
    label: string | JSX.Element,
    align: 'left' | 'right' = 'right',
    sortKey?: KeywordTableSortKey,
  ) => (
    <th
      className={`relative py-1.5 font-medium ${align === 'left' ? 'text-left' : 'text-right'}`}
      style={{
        width: columnWidths[columnKey],
        minWidth: MIN_KEYWORD_COLUMN_WIDTHS[columnKey],
      }}
    >
      {sortKey ? (
        <button
          type="button"
          onClick={() => toggleSort(sortKey)}
          className={`inline-flex w-full items-center gap-1 ${
            align === 'left' ? 'justify-start' : 'justify-end'
          } hover:text-foreground`}
        >
          <span>{label}</span>
          <span className="text-[10px]">
            {keywordSort.key === sortKey
              ? keywordSort.direction === 'asc'
                ? '↑'
                : '↓'
              : '↕'}
          </span>
        </button>
      ) : (
        label
      )}
      <button
        type="button"
        aria-label={`Resize ${typeof label === 'string' ? label : columnKey} column`}
        className="absolute right-0 top-1/2 h-5 w-2 -translate-y-1/2 cursor-col-resize rounded hover:bg-teal/20"
        onPointerDown={(event) => resizeColumn(columnKey, event)}
      />
    </th>
  );

  // Two flavors of gap (Phase F2):
  //   - landing_page is null → "No URL assigned" (pipeline didn't pick one)
  //   - landing_page is set but not in tenant's published pages →
  //     "URL not published" (operator typed it as a candidate but
  //     no service page actually lives there yet)
  const gapKind = !cluster.landing_page ? 'no-url' : (isGap ? 'unpublished' : null);

  return (
    <div
      className={
        gapKind
          ? 'rounded border border-coral/30 bg-coral-light/20 p-4'
          : 'rounded border border-border bg-warm-bg p-4'
      }
    >
      <div
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        aria-label={`${expanded ? 'Collapse' : 'Expand'} cluster ${cluster.cluster_name}`}
        className={`flex cursor-pointer select-none items-start justify-between ${expanded ? 'mb-3' : ''}`}
        onClick={onToggleExpanded}
        onKeyDown={(event) => {
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onToggleExpanded();
          }
        }}
      >
        <div>
          <div className="flex items-center gap-2">
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${
                expanded ? '' : '-rotate-90'
              }`}
            />
            <span className="font-medium">{cluster.cluster_name}</span>
            {isNameMatchSingleton && (
              <Badge
                variant="outline"
                className="border-coral/40 bg-coral-light text-coral-dark text-[10px]"
              >
                <TriangleAlert className="mr-1 h-2.5 w-2.5" />
                Thin cluster
              </Badge>
            )}
            {typeof cluster.used === 'boolean' && (
              <Badge
                variant="outline"
                className={
                  cluster.used
                    ? 'border-teal/30 bg-teal-light text-teal-dark text-[10px]'
                    : 'border-coral/40 bg-coral-light text-coral-dark text-[10px]'
                }
                title={
                  cluster.used_state
                    ? `Used in campaign state ${cluster.used_state}${
                        cluster.used_in_campaign_run_id
                          ? ` (run ${cluster.used_in_campaign_run_id})`
                          : ''
                      }`
                    : cluster.used_in_campaign_run_id
                      ? `Campaign run ${cluster.used_in_campaign_run_id}`
                      : cluster.used
                        ? 'Used in campaign'
                        : 'Not used in campaign'
                }
              >
                {cluster.used ? (
                  <CheckCircle2 className="mr-1 h-2.5 w-2.5" />
                ) : (
                  <XCircle className="mr-1 h-2.5 w-2.5" />
                )}
                {cluster.used ? 'Used' : 'Not used'}
              </Badge>
            )}
            {gapKind && (
              <Badge
                variant="outline"
                className="border-coral/40 bg-coral-light text-coral-dark text-[10px]"
              >
                <TriangleAlert className="mr-1 h-2.5 w-2.5" />
                Content gap
              </Badge>
            )}
          </div>
          {cluster.landing_page ? (
            <div className="mt-0.5 text-xs">
              <span className="text-muted-foreground">→ </span>
              <a
                href={cluster.landing_page}
                target="_blank"
                rel="noreferrer"
                className="text-teal-dark hover:underline"
                onClick={(event) => event.stopPropagation()}
              >
                {cluster.landing_page}
              </a>
              {gapKind === 'unpublished' && (
                <span className="ml-2 text-coral-dark">(not yet published)</span>
              )}
            </div>
          ) : (
            <div className="mt-0.5 flex items-center gap-1 text-xs text-coral-dark">
              <TriangleAlert className="h-3 w-3" />
              No landing page assigned to this cluster
            </div>
          )}
        </div>
        <div className="flex max-w-sm flex-wrap justify-end gap-1 text-xs">
          <Badge variant="outline" className="bg-card">
            vol {totalVolume.toLocaleString()}/mo
          </Badge>
          <Badge variant="outline" className="bg-card">
            avg CPC ${avgCpc.toFixed(2)}
          </Badge>
          <Badge
            variant="outline"
            className={
              plannerCount > 0
                ? 'border-teal/30 bg-card text-teal-dark'
                : 'border-coral/40 bg-coral-light/20 text-coral-dark'
            }
          >
            Planner {plannerCount}/{cluster.keywords.length}
          </Badge>
          <Badge variant="outline" className="bg-card">
            Bids {bidRangeCount}/{cluster.keywords.length}
          </Badge>
          <Badge variant="outline" className="bg-card">
            Competition {competitionCount}/{cluster.keywords.length}
          </Badge>
          {newKeywordCount > 0 && (
            <Badge
              variant="outline"
              className="border-teal/30 bg-teal-light/20 text-teal-dark"
              title="Keywords not found in the connected Google Ads account"
            >
              New {newKeywordCount}/{cluster.keywords.length}
            </Badge>
          )}
          {existingKeywordCount > 0 && (
            <Badge
              variant="outline"
              className="border-coral/30 bg-coral-light/20 text-coral-dark"
              title="Keywords already present in the connected Google Ads account"
            >
              Existing {existingKeywordCount}/{cluster.keywords.length}
            </Badge>
          )}
          {unknownKeywordCount > 0 && (
            <Badge
              variant="outline"
              className="bg-card text-muted-foreground"
              title="Could not confirm account membership for some keywords"
            >
              Unknown {unknownKeywordCount}/{cluster.keywords.length}
            </Badge>
          )}
          {trendCount > 0 && (
            <Badge variant="outline" className="bg-card">
              Trend {trendCount}/{cluster.keywords.length}
            </Badge>
          )}
        </div>
      </div>

      {expanded && (
        <>
      <div className="mb-3 flex flex-wrap items-center gap-2">
        {gapKind && (
          <span className="text-[11px] text-muted-foreground">
            {gapKind === 'no-url'
              ? 'Demand exists, no page targeting it.'
              : 'Demand exists, page not published yet.'}
          </span>
        )}
        {/* Phase D — link out to /ads-tools Competitive tab so the
            operator can see which competitors won impressions on these
            terms in their account's Auction Insights. */}
        <button
          type="button"
          onClick={() => navigate('/ads-tools/competitive?platform=google-ads')}
          className="ml-auto inline-flex items-center gap-1 rounded border border-teal/40 bg-card px-2.5 py-1 text-xs font-medium text-teal-dark hover:bg-teal-light"
          title="View competitors winning auctions on these terms"
        >
          <Swords className="h-3 w-3" />
          See competitors
          <ExternalLink className="h-2.5 w-2.5 opacity-60" />
        </button>
      </div>
      {clusterTrendData.length >= 2 && (
        <div className="mb-3">
          <KeywordPlannerTrendChart
            data={clusterTrendData}
            title={`${cluster.cluster_name} demand trend`}
            description="Monthly searches from Google Ads rows in this ad group."
            compact
          />
        </div>
      )}
      <div className="mb-2 flex flex-wrap items-center gap-2 rounded border border-border bg-card p-2">
        <div className="relative min-w-[220px] flex-1 sm:max-w-sm">
          <Search className="pointer-events-none absolute left-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={keywordSearch}
            onChange={(event) => setKeywordSearch(event.target.value)}
            placeholder="Filter keywords in this cluster..."
            className="h-8 pl-7 text-xs"
          />
        </div>
        <Select
          value={keywordFilter}
          onValueChange={(value) => setKeywordFilter(value as KeywordTableFilter)}
        >
          <SelectTrigger className="h-8 w-[180px] bg-card text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All keywords</SelectItem>
            <SelectItem value="selected">Selected only</SelectItem>
            <SelectItem value="low_fruit">Low fruit</SelectItem>
            <SelectItem value="new">New to account</SelectItem>
            <SelectItem value="planner">Keyword Planner rows</SelectItem>
            <SelectItem value="has_trend">Has trend data</SelectItem>
            <SelectItem value="missing_bid">Missing bid data</SelectItem>
          </SelectContent>
        </Select>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 text-xs"
          onClick={() => setColumnWidths(DEFAULT_KEYWORD_COLUMN_WIDTHS)}
        >
          Reset columns
        </Button>
        <span className="ml-auto text-xs text-muted-foreground">
          {visibleKeywords.length}/{cluster.keywords.length} shown · {visibleSelectedCount} selected
        </span>
      </div>
      <div className="overflow-x-auto">
        <table
          className="w-full table-fixed text-sm"
          style={{
            minWidth: KEYWORD_TABLE_COLUMNS.reduce(
              (total, columnKey) => total + columnWidths[columnKey],
              0,
            ),
          }}
        >
          <colgroup>
            {KEYWORD_TABLE_COLUMNS.map((columnKey) => (
              <col
                key={columnKey}
                style={{ width: columnWidths[columnKey] }}
              />
            ))}
          </colgroup>
          <thead className="text-xs text-muted-foreground">
            <tr className="border-b border-border/50">
              {renderHeader(
                'use',
                <Checkbox
                  aria-label={
                    allVisibleSelected
                      ? 'Clear visible keywords in this cluster'
                      : 'Select visible keywords in this cluster'
                  }
                  checked={
                    allVisibleSelected
                      ? true
                      : someVisibleSelected
                        ? 'indeterminate'
                        : false
                  }
                  disabled={visibleKeywords.length === 0}
                  onCheckedChange={(checked) =>
                    toggleAllVisibleKeywords(checked === true)
                  }
                />,
                'left',
              )}
              {renderHeader('keyword', 'Keyword', 'left', 'keyword')}
              {renderHeader('volume', 'Volume', 'right', 'volume')}
              {renderHeader('cpc', 'CPC', 'right', 'cpc')}
              {renderHeader('trend', 'Trend', 'right', 'trend')}
              {renderHeader('lowFruit', 'Low fruit', 'right', 'lowFruit')}
              {renderHeader('competition', 'Competition', 'right', 'competition')}
              {renderHeader(
                'bid',
                <HeaderTooltip
                  label="Top of page bid"
                  description="Google's estimated low-high bid range for top-of-page ad placement. Blank means Google did not return bid data for this keyword."
                />,
                'right',
                'bid',
              )}
              {renderHeader(
                'account',
                <HeaderTooltip
                  label="Account"
                  description="Whether this keyword already exists in the connected Google Ads account. New means it was not found; unknown means the lookup could not confirm it."
                />,
                'right',
                'account',
              )}
              {renderHeader('source', 'Source', 'right', 'source')}
            </tr>
          </thead>
          <tbody>
            {visibleKeywords.length === 0 && (
              <tr>
                <td
                  colSpan={KEYWORD_TABLE_COLUMNS.length}
                  className="py-6 text-center text-xs text-muted-foreground"
                >
                  No keywords match this filter.
                </td>
              </tr>
            )}
            {visibleKeywords.map((kw) => (
              <tr key={kw.keyword} className="border-b border-border/30 last:border-0">
                <td className="py-1.5">
                  <Checkbox
                    aria-label={`Use ${kw.keyword}`}
                    checked={selectedKeywordKeys.has(keywordSelectionKey(kw.keyword))}
                    onCheckedChange={(checked) =>
                      onKeywordToggle(kw.keyword, checked === true)
                    }
                  />
                </td>
                <td className="py-1.5">{kw.keyword}</td>
                <td className="py-1.5 text-right tabular-nums">
                  {kw.volume.toLocaleString()}
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  ${kw.cpc.toFixed(2)}
                </td>
                <td className="py-1.5 text-right">
                  <KeywordTrendSparkline keyword={kw} />
                </td>
                <td className="py-1.5 text-right">
                  <LowHangingFruitBadge
                    signal={keywordLowHangingFruitSignal(kw, benchmarks)}
                  />
                </td>
                <td className="py-1.5 text-right">
                  <CompetitionBadge value={kw.kp_competition ?? null} />
                </td>
                <td className="py-1.5 text-right tabular-nums">
                  <BidRange
                    low={kw.kp_low_top_of_page_bid_micros ?? null}
                    high={kw.kp_high_top_of_page_bid_micros ?? null}
                  />
                </td>
                <td className="py-1.5 text-right">
                  <AccountMembershipBadge value={kw.in_account ?? null} />
                </td>
                <td className="py-1.5 text-right">
                  <SourceBadge value={kw.source ?? null} />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
        </>
      )}
    </div>
  );
}


function KeywordPlannerTrendChart({
  data,
  title = 'Keyword Planner demand trend',
  description = 'Aggregated monthly searches from Google Ads rows returned for this plan.',
  compact = false,
}: {
  data: PlannerTrendPoint[];
  title?: string;
  description?: string;
  compact?: boolean;
}) {
  const last = data[data.length - 1];
  const peak = data.reduce(
    (max, point) => Math.max(max, point.searches),
    0,
  );
  return (
    <div className="rounded border border-border bg-card p-3">
      <div className="mb-2 flex flex-wrap items-end justify-between gap-2">
        <div>
          <div className="text-sm font-medium">{title}</div>
          <div className="text-xs text-muted-foreground">
            {description}
          </div>
        </div>
        <div className="flex gap-2 text-xs">
          <Badge variant="outline" className="bg-warm-bg">
            Latest {last.searches.toLocaleString()}/mo
          </Badge>
          <Badge variant="outline" className="bg-warm-bg">
            Peak {peak.toLocaleString()}/mo
          </Badge>
        </div>
      </div>
      <div className={compact ? 'h-28 min-h-28' : 'h-48 min-h-48'}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 8, right: 16, left: compact ? 0 : 0, bottom: 0 }}
          >
            <CartesianGrid strokeDasharray="3 3" stroke="#e7e1d7" />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11 }}
              tickLine={false}
              axisLine={false}
            />
            {!compact && (
              <YAxis
                width={48}
                tick={{ fontSize: 11 }}
                tickFormatter={(value) => Number(value).toLocaleString()}
                tickLine={false}
                axisLine={false}
              />
            )}
            <Tooltip
              formatter={(value) => [
                Number(value).toLocaleString(),
                'Monthly searches',
              ]}
              labelClassName="text-xs"
            />
            <Line
              type="monotone"
              dataKey="searches"
              stroke="#0f766e"
              strokeWidth={2.5}
              dot={{ r: 3 }}
              activeDot={{ r: 5 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}


function KeywordTrendSparkline({ keyword }: { keyword: PreviewedKeyword }) {
  const data = buildKeywordPlannerTrendData([
    {
      cluster_name: keyword.keyword,
      landing_page: null,
      keywords: [keyword],
    },
  ]);
  if (data.length < 2) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  const width = 84;
  const height = 24;
  const values = data.map((point) => point.searches);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = Math.max(1, max - min);
  const points = data.map((point, index) => {
    const x = data.length === 1 ? width / 2 : (index / (data.length - 1)) * width;
    const y = height - ((point.searches - min) / range) * (height - 4) - 2;
    return { ...point, x, y };
  });
  const path = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x.toFixed(2)} ${point.y.toFixed(2)}`)
    .join(' ');
  const latest = data[data.length - 1];
  const peak = data.reduce(
    (maxPoint, point) => (point.searches > maxPoint.searches ? point : maxPoint),
    data[0],
  );
  return (
    <TooltipProvider delayDuration={120}>
      <UiTooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            className="inline-flex h-7 w-[92px] items-center justify-end rounded border border-transparent px-1 hover:border-teal/30 hover:bg-card"
            aria-label={`${keyword.keyword} monthly search trend`}
          >
            <svg
              width={width}
              height={height}
              viewBox={`0 0 ${width} ${height}`}
              role="img"
              aria-hidden="true"
              className="overflow-visible"
            >
              <path d={path} fill="none" stroke="#0f766e" strokeWidth="2" />
              {points.map((point) => (
                <circle
                  key={point.key}
                  cx={point.x}
                  cy={point.y}
                  r={point.key === latest.key ? 2.6 : 1.6}
                  fill="#0f766e"
                />
              ))}
            </svg>
          </button>
        </TooltipTrigger>
        <TooltipContent
          side="left"
          align="center"
          className="w-[420px] max-w-[calc(100vw-2rem)] p-3 text-xs"
        >
          <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
            <div>
              <div className="font-medium text-foreground">{keyword.keyword}</div>
              <div className="text-[11px] text-muted-foreground">
                Keyword Planner monthly searches
              </div>
            </div>
            <div className="flex flex-wrap justify-end gap-1.5">
              <Badge variant="outline" className="bg-card text-[10px]">
                Latest {latest.searches.toLocaleString()}
              </Badge>
              <Badge variant="outline" className="bg-card text-[10px]">
                Peak {peak.searches.toLocaleString()}
              </Badge>
            </div>
          </div>
          <div className="h-44 min-h-44">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={data} margin={{ top: 8, right: 8, left: 2, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#d6e7e4" vertical={false} />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10 }}
                  tickLine={false}
                  axisLine={false}
                  interval="preserveStartEnd"
                />
                <YAxis
                  width={48}
                  tick={{ fontSize: 10 }}
                  tickFormatter={(value) => Number(value).toLocaleString()}
                  tickLine={false}
                  axisLine={false}
                />
                <Tooltip
                  formatter={(value) => [
                    Number(value).toLocaleString(),
                    'Monthly searches',
                  ]}
                />
                <Line
                  type="monotone"
                  dataKey="searches"
                  stroke="#0f766e"
                  strokeWidth={2.5}
                  dot={{ r: 3, fill: '#0f766e', strokeWidth: 0 }}
                  activeDot={{ r: 5 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
          <div className="mt-3 grid grid-cols-3 gap-x-4 gap-y-1">
            {data.map((point) => (
              <span key={point.key} className="flex items-center justify-between gap-2">
                <span className="text-muted-foreground">{point.label}</span>
                <span className="tabular-nums text-foreground">
                  {point.searches.toLocaleString()}
                </span>
              </span>
            ))}
          </div>
        </TooltipContent>
      </UiTooltip>
    </TooltipProvider>
  );
}


function TargetKeywordSeedsPanel({
  suggestions,
}: {
  suggestions: TargetKeywordSuggestion[];
}) {
  const operatorCount = suggestions.filter((s) => s.source.startsWith('operator')).length;
  const sourceLabel =
    operatorCount === suggestions.length
      ? 'manual'
      : operatorCount > 0
        ? 'manual + workspace'
        : 'workspace-derived';
  return (
    <div className="rounded border border-teal/30 bg-teal-light/20 p-3">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Seed keywords used</span>
        <Badge variant="outline" className="bg-card text-[10px]">
          {sourceLabel}
        </Badge>
        <span className="text-xs text-muted-foreground">
          Sent into Keyword Planner before filtering and clustering.
        </span>
      </div>
      <div className="flex flex-wrap gap-2">
        {suggestions.slice(0, 16).map((suggestion) => (
          <span
            key={`${suggestion.source}-${suggestion.keyword}`}
            className="inline-flex items-center gap-1 rounded border border-border/70 bg-card px-2 py-1 text-xs"
            title={suggestion.reason ?? suggestion.source}
          >
            <span className="font-medium">{suggestion.keyword}</span>
            {suggestion.service && (
              <span className="text-muted-foreground">
                from {suggestion.service}
              </span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}


/**
 * The suggested keywords, shown by default (comms 9/23). The internal step
 * log that used to sit beside them is not shown: it is processing detail,
 * not something a user acts on. Staging keeps its prior-use badges, so an
 * idea already researched for this workspace says so.
 */
function SuggestedSeedsPanel({
  suggestions,
  isLoading,
  isError,
  priorUse = {},
}: {
  suggestions: TargetKeywordSuggestion[];
  isLoading: boolean;
  isError: boolean;
  /** Keyed by keyword. A missing entry means never researched here. */
  priorUse?: Record<string, PriorKeywordUse>;
}) {
  return (
    <div className="rounded border border-border bg-card p-3">
      <div className="mb-2 flex items-center gap-2">
        <span className="text-sm font-medium">Suggested keywords</span>
        <Badge variant="outline" className="text-[10px]">
          {suggestions.length}
        </Badge>
      </div>
      {suggestions.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {suggestions.map((suggestion) => (
            <span
              key={`${suggestion.source}-${suggestion.keyword}`}
              className="inline-flex items-center gap-1 rounded border border-border/70 bg-warm-bg px-2 py-1 text-xs"
              title={suggestion.reason ?? suggestion.source}
            >
              <span className="font-medium">{suggestion.keyword}</span>
              <span className="text-muted-foreground">
                {suggestion.source.replace(/_/g, ' ')}
              </span>
              {priorUse[suggestion.keyword] &&
                !priorUse[suggestion.keyword].is_new && (
                  <Badge
                    variant="outline"
                    className="ml-0.5 border-teal/40 bg-teal-light/40 text-[9px] text-teal-dark"
                    title={
                      priorUse[suggestion.keyword].last_seen
                        ? `Last researched ${new Date(
                            priorUse[suggestion.keyword].last_seen as string,
                          ).toLocaleDateString()} · ${
                            priorUse[suggestion.keyword].run_count
                          } run(s)`
                        : undefined
                    }
                  >
                    {priorUse[suggestion.keyword].returned_as_keyword
                      ? 'in a past run'
                      : 'tried, no results'}
                  </Badge>
                )}
            </span>
          ))}
        </div>
      ) : isLoading ? (
        <p className="flex items-center gap-2 text-xs text-muted-foreground">
          <Loader2 className="h-3 w-3 animate-spin" />
          Finding keyword ideas…
        </p>
      ) : isError ? (
        <p className="text-xs text-muted-foreground">
          Could not load keyword ideas. Click Find keyword ideas to try again.
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">
          No ideas yet. Add services in the Brand Bible or type your own
          keywords below.
        </p>
      )}
    </div>
  );
}


function SitePageSuggestionReviewPanel({
  suggestions,
}: {
  suggestions: SitePageSuggestion[];
}) {
  return (
    <div className="mt-2 rounded border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Suggested pages</span>
        </div>
        <Badge variant="outline" className="text-[10px]">
          {suggestions.length} suggested
        </Badge>
      </div>
      {suggestions.length > 0 && (
        <div className="max-h-40 divide-y divide-border overflow-y-auto">
          {suggestions.map((suggestion) => (
            <div
              key={`${suggestion.source}-${suggestion.url}`}
              className="grid grid-cols-[minmax(0,1fr)_56px] gap-2 px-3 py-2 text-xs"
            >
              <div className="min-w-0">
                <div className="truncate font-medium">
                  {suggestion.title || suggestion.url}
                </div>
                <a
                  href={suggestion.url}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 block truncate text-teal-dark hover:underline"
                >
                  {suggestion.url}
                </a>
                <div className="mt-0.5 truncate text-muted-foreground">
                  {suggestion.reason}
                </div>
              </div>
              <div className="text-right">
                <Badge variant="outline" className="bg-warm-bg text-[10px]">
                  {suggestion.score}
                </Badge>
                <div className="mt-1 truncate text-[10px] text-muted-foreground">
                  {suggestion.source.replace(/_/g, ' ')}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


function QualityChecksPanel({
  checks,
  summary,
}: {
  checks: KeywordResearchQualityCheck[];
  summary: ReturnType<typeof summarizeQualityChecks>;
}) {
  const navigate = useNavigate();
  const hasIssues = summary.warning > 0 || summary.fail > 0;
  return (
    <div
      className={
        hasIssues
          ? 'rounded border border-coral/30 bg-coral-light/20 p-3'
          : 'rounded border border-teal/30 bg-teal-light/20 p-3'
      }
    >
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <span className="text-sm font-medium">Quality gates</span>
        <Badge variant="outline" className="bg-card text-[10px]">
          {summary.pass} pass
        </Badge>
        {summary.warning > 0 && (
          <Badge
            variant="outline"
            className="border-coral/40 bg-card text-[10px] text-coral-dark"
          >
            {summary.warning} warning{summary.warning !== 1 ? 's' : ''}
          </Badge>
        )}
        {summary.fail > 0 && (
          <Badge
            variant="outline"
            className="border-destructive/30 bg-card text-[10px] text-destructive"
          >
            {summary.fail} fail{summary.fail !== 1 ? 's' : ''}
          </Badge>
        )}
      </div>
      <div className="grid gap-2 md:grid-cols-2">
        {checks.map((check) => {
          const action = getQualityCheckAction(check);
          return (
            <div
              key={check.id}
              className="rounded border border-border/60 bg-card px-3 py-2"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-xs font-medium">{check.label}</span>
                <QualityStatusBadge status={check.status} />
              </div>
              <p className="mt-1 text-[11px] leading-relaxed text-muted-foreground">
                {check.evidence}
              </p>
              {action && (
                <button
                  type="button"
                  onClick={() => navigate(action.href)}
                  className="mt-2 inline-flex items-center gap-1 rounded border border-teal/40 bg-card px-2.5 py-1 text-xs font-medium text-teal-dark hover:bg-teal-light"
                >
                  {action.label}
                  <ExternalLink className="h-2.5 w-2.5 opacity-60" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function getQualityCheckAction(check: KeywordResearchQualityCheck):
  | { href: string; label: string }
  | null {
  if (check.status === 'pass') return null;
  if (check.id !== 'keyword_planner_readiness') return null;

  const evidence = check.evidence.toLowerCase();
  if (evidence.includes('google_ads_keyword_planner_enabled')) {
    return { href: '/workspace-settings?tab=integrations', label: 'Review Google Ads setup' };
  }

  return { href: '/workspace-settings?tab=integrations', label: 'Open Google Ads settings' };
}


function QualityStatusBadge({ status }: { status: string }) {
  const normalized = status.toLowerCase();
  const className =
    normalized === 'pass'
      ? 'border-teal/40 bg-teal-light text-teal-dark'
      : normalized === 'fail'
        ? 'border-destructive/30 bg-destructive/10 text-destructive'
        : 'border-coral/40 bg-coral-light text-coral-dark';
  const label =
    normalized === 'pass' ? 'Pass' : normalized === 'fail' ? 'Fail' : 'Warn';
  return (
    <Badge variant="outline" className={`text-[10px] ${className}`}>
      {label}
    </Badge>
  );
}


function summarizeQualityChecks(checks: KeywordResearchQualityCheck[]) {
  return checks.reduce(
    (acc, check) => {
      const status = check.status.toLowerCase();
      if (status === 'pass') acc.pass += 1;
      else if (status === 'fail') acc.fail += 1;
      else acc.warning += 1;
      return acc;
    },
    { pass: 0, warning: 0, fail: 0 },
  );
}


function downloadClustersCsv(clusters: PreviewedCluster[], tenantId: string) {
  const csv = buildClustersCsv(clusters);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  const tenant = workspaceDomain(tenantId) || 'keyword-research';
  link.href = url;
  link.download = `${tenant}-google-ads-keyword-clusters.csv`;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}


async function copyClustersCsv(clusters: PreviewedCluster[]) {
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard API unavailable');
  }
  await navigator.clipboard.writeText(buildClustersCsv(clusters));
}


function buildApprovalEmailBody(
  clusters: PreviewedCluster[],
  negatives: NegativeKeywordCandidate[],
  tenantId: string,
  qualityChecks: KeywordResearchQualityCheck[] = [],
  targetKeywordSuggestions: TargetKeywordSuggestion[] = [],
  plannerLanguageLabel = 'Auto/default',
  plannerGeoLabel = 'Worldwide/default',
  plannerSiteFilterUrl: string | null = null,
): string {
  const tenant = workspaceDomain(tenantId) || tenantId || 'this workspace';
  const totalKeywords = clusters.reduce((n, c) => n + c.keywords.length, 0);
  const totalVolume = clusters.reduce(
    (n, c) => n + c.keywords.reduce((m, kw) => m + kw.volume, 0),
    0,
  );
  const totalCpc = clusters.reduce(
    (n, c) => n + c.keywords.reduce((m, kw) => m + kw.cpc, 0),
    0,
  );
  const avgCpc = totalKeywords > 0 ? totalCpc / totalKeywords : 0;

  const lines = [
    `Please review the Google Ads keyword cluster plan for ${tenant}.`,
    '',
    `Summary: ${clusters.length} ad groups, ${totalKeywords} keywords, ${totalVolume.toLocaleString()} combined monthly search volume, average CPC $${avgCpc.toFixed(2)}.`,
    `Keyword Planner targeting: ${plannerLanguageLabel}; ${plannerGeoLabel}; site filter ${plannerSiteFilterUrl || 'none'}.`,
    '',
    'Recommended ad groups:',
  ];

  if (qualityChecks.length > 0) {
    const summary = summarizeQualityChecks(qualityChecks);
    lines.push(
      '',
      `Quality gates: ${summary.pass} pass, ${summary.warning} warning, ${summary.fail} fail.`,
    );
    qualityChecks.forEach((check) => {
      lines.push(`- ${check.label}: ${check.status} - ${check.evidence}`);
    });
    lines.push('');
  }

  if (targetKeywordSuggestions.length > 0) {
    lines.push(
      'Seed keywords used:',
      targetKeywordSuggestions
        .slice(0, 16)
        .map((suggestion) => suggestion.keyword)
        .join(', '),
      '',
    );
  }

  clusters.forEach((cluster, index) => {
    const clusterVolume = cluster.keywords.reduce((n, kw) => n + kw.volume, 0);
    const clusterAvgCpc =
      cluster.keywords.length > 0
        ? cluster.keywords.reduce((n, kw) => n + kw.cpc, 0) / cluster.keywords.length
        : 0;
    const keywords = cluster.keywords
      .slice(0, 15)
      .map((kw) => `${kw.keyword} (${kw.volume.toLocaleString()}/mo, $${kw.cpc.toFixed(2)})`)
      .join('; ');
    lines.push(
      `${index + 1}. ${cluster.cluster_name}`,
      `   Landing page: ${cluster.landing_page || 'Needs landing page assignment'}`,
      `   Cluster totals: ${cluster.keywords.length} keywords, ${clusterVolume.toLocaleString()}/mo, avg CPC $${clusterAvgCpc.toFixed(2)}`,
      `   Keywords: ${keywords}`,
    );
  });

  if (negatives.length > 0) {
    lines.push('', `Suggested negative keywords (${negatives.length}):`);
    negatives.slice(0, 25).forEach((negative) => {
      const metrics = [
        negative.volume != null ? `${negative.volume.toLocaleString()}/mo` : null,
        negative.cpc != null ? `$${negative.cpc.toFixed(2)}` : null,
      ].filter(Boolean).join(', ');
      lines.push(
        `- ${negative.keyword} (${negative.reason}${metrics ? `, ${metrics}` : ''})`,
      );
    });
    if (negatives.length > 25) {
      lines.push(`- plus ${negatives.length - 25} more in the exported negative keyword list`);
    }
  }

  lines.push(
    '',
    'Approval request: approve this plan, reject it, or reply with keyword, landing-page, or negative-keyword changes.',
  );

  return lines.join('\n');
}


async function copyApprovalEmail(
  clusters: PreviewedCluster[],
  negatives: NegativeKeywordCandidate[],
  tenantId: string,
  qualityChecks: KeywordResearchQualityCheck[] = [],
  targetKeywordSuggestions: TargetKeywordSuggestion[] = [],
  plannerLanguageLabel = 'Auto/default',
  plannerGeoLabel = 'Worldwide/default',
  plannerSiteFilterUrl: string | null = null,
) {
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard API unavailable');
  }
  await navigator.clipboard.writeText(
    buildApprovalEmailBody(
      clusters,
      negatives,
      tenantId,
      qualityChecks,
      targetKeywordSuggestions,
      plannerLanguageLabel,
      plannerGeoLabel,
      plannerSiteFilterUrl,
    ),
  );
}

function buildSponsoredAdNote(data: {
  keyword?: string | null;
  sponsored_results: Array<{
    title: string;
    domain?: string | null;
    display_url?: string | null;
  }>;
}): string {
  const top = data.sponsored_results[0];
  const label = top?.display_url || top?.domain || top?.title || 'sponsored block';
  return `${data.keyword || 'query'}: ${label}`;
}

async function copySponsoredAdBlock(data: {
  keyword?: string | null;
  sponsored_results: Array<{
    title: string;
    url?: string | null;
    display_url?: string | null;
    snippet?: string | null;
    sitelinks: string[];
    position?: number | null;
    domain?: string | null;
  }>;
}) {
  if (!navigator.clipboard?.writeText) {
    throw new Error('Clipboard API unavailable');
  }
  const lines = [
    `Sponsored results for ${data.keyword || 'query'}`,
    '',
    ...data.sponsored_results.flatMap((ad, index) => [
      `${index + 1}. ${ad.title}`,
      `   URL: ${ad.url || ad.display_url || ad.domain || '—'}`,
      ad.snippet ? `   Snippet: ${ad.snippet}` : null,
      ad.sitelinks.length ? `   Sitelinks: ${ad.sitelinks.join(' | ')}` : null,
    ].filter((line): line is string => Boolean(line))),
  ];
  await navigator.clipboard.writeText(lines.join('\n'));
}


/** Color-coded badge for Google Keyword Planner competition level. */
function CompetitionBadge({ value }: { value: string | null }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  const v = value.toUpperCase();
  const styles: Record<string, string> = {
    LOW: 'bg-teal-light text-teal-dark border-teal/40',
    MEDIUM: 'bg-coral-light/60 text-coral-dark border-coral/40',
    HIGH: 'bg-destructive/10 text-destructive border-destructive/30',
  };
  const label = v === 'LOW' ? 'Low' : v === 'MEDIUM' ? 'Med' : v === 'HIGH' ? 'High' : v;
  return (
    <Badge variant="outline" className={`text-[10px] ${styles[v] ?? 'bg-warm-bg'}`}>
      {label}
    </Badge>
  );
}


function SourceBadge({ value }: { value: string | null }) {
  const normalized = value ?? '';
  const label =
    normalized === 'keyword_planner'
      ? 'Keyword Planner'
      : normalized === 'brainstorm'
        ? 'Seed fallback'
        : 'Seed';
  const className =
    normalized === 'keyword_planner'
      ? 'bg-teal-light text-teal-dark border-teal/40'
      : 'bg-warm-bg text-muted-foreground border-border';
  return (
    <Badge variant="outline" className={`whitespace-nowrap text-[10px] ${className}`}>
      {label}
    </Badge>
  );
}


function AccountMembershipBadge({ value }: { value: boolean | null }) {
  if (value == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  return (
    <Badge
      variant="outline"
      className={
        value
          ? 'bg-coral-light/50 text-coral-dark border-coral/40 text-[10px]'
          : 'bg-teal-light/50 text-teal-dark border-teal/40 text-[10px]'
      }
    >
      {value ? 'Existing' : 'New'}
    </Badge>
  );
}


/** Convert micros (millionths of a USD) into "$X.XX-$Y.YY" string. */
function BidRange({ low, high }: { low: number | null; high: number | null }) {
  if (low == null && high == null) {
    return <span className="text-muted-foreground">—</span>;
  }
  const fmt = (m: number) => `$${(m / 1_000_000).toFixed(2)}`;
  if (low != null && high != null) return <>{fmt(low)} – {fmt(high)}</>;
  if (low != null) return <>≥{fmt(low)}</>;
  return <>≤{fmt(high!)}</>;
}

/**
 * Phase D.2 — Brand-defense banner.
 *
 * Renders ABOVE the clusters card when the brand-defense probe finds
 * competitors bidding on the tenant's brand name. Per the expert
 * debrief: this is one of the two signals that justify spinning up
 * a brand-defense campaign (the other is brand search volume).
 */
function BrandDefenseBanner({
  data,
}: {
  data: import('@/services/googleAdsCampaignsService').BrandDefenseProbeResult;
}) {
  const navigate = useNavigate();
  if (!data.enabled || !data.competitors_bidding) return null;
  return (
    <div className="rounded border border-destructive/30 bg-destructive/5 p-4">
      <div className="flex items-start gap-3">
        <TriangleAlert className="mt-0.5 h-5 w-5 flex-shrink-0 text-destructive" />
        <div className="flex-1 space-y-2">
          <div>
            <div className="font-medium text-destructive">
              {data.competitor_count} competitor
              {data.competitor_count !== 1 ? 's are' : ' is'} bidding on{' '}
              <code className="rounded bg-card px-1.5 py-0.5 text-sm">
                {data.brand_name}
              </code>
            </div>
            <p className="text-sm text-muted-foreground">
              Competitors will win your brand-name impressions unless you
              run a defensive brand campaign. Each captured click is a
              lost lead that searched for you by name.
            </p>
          </div>
          {data.competitors_seen.length > 0 && (
            <div className="text-xs">
              <span className="text-muted-foreground">Seen on SERP: </span>
              {data.competitors_seen.slice(0, 5).map((c, i) => (
                <span key={c.domain}>
                  {i > 0 && ', '}
                  <a
                    href={c.link}
                    target="_blank"
                    rel="noreferrer"
                    className="text-destructive hover:underline"
                  >
                    {c.domain}
                  </a>
                </span>
              ))}
              {data.competitors_seen.length > 5 && (
                <span className="text-muted-foreground">
                  {' '}+{data.competitors_seen.length - 5} more
                </span>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={() => navigate('/ads-tools/competitive?platform=google-ads')}
            className="inline-flex items-center gap-1 rounded border border-destructive/40 bg-card px-2.5 py-1 text-xs font-medium text-destructive hover:bg-destructive/10"
          >
            <Swords className="h-3 w-3" />
            See competitive intelligence
            <ExternalLink className="h-2.5 w-2.5 opacity-60" />
          </button>
        </div>
      </div>
    </div>
  );
}


function splitLines(value: string): string[] {
  return value
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

function formatRelativeTime(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return 'just now';
  const secondsAgo = Math.max(0, (Date.now() - then) / 1000);
  if (secondsAgo < 45) return 'just now';
  if (secondsAgo < 90) return '1 min ago';
  const minutesAgo = Math.round(secondsAgo / 60);
  if (minutesAgo < 60) return `${minutesAgo} min ago`;
  const hoursAgo = Math.round(minutesAgo / 60);
  if (hoursAgo < 24) return `${hoursAgo} hr ago`;
  const daysAgo = Math.round(hoursAgo / 24);
  if (daysAgo < 30) return `${daysAgo} day${daysAgo !== 1 ? 's' : ''} ago`;
  const monthsAgo = Math.round(daysAgo / 30);
  if (monthsAgo < 12) return `${monthsAgo} mo ago`;
  const yearsAgo = Math.round(monthsAgo / 12);
  return `${yearsAgo} yr${yearsAgo !== 1 ? 's' : ''} ago`;
}
